import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Format, Post, PostStatus } from '../types';
import {
  DEMO_NOW,
  DEMO_TODAY,
  LATE_WINDOW_MINUTES,
  seedMembers,
  seedStaff,
  seedAccounts,
  seedAssets,
  seedBrands,
  seedCampaigns,
  seedConversations,
  seedDrive,
  seedNotices,
  seedPosts,
  seedSettings,
  seedTasks,
  seedTemplates,
  seedUploads,
  workspaces,
  seedBusiness,
  seedAi,
  seedOnboarding,
  preferencesFor,
  onAccount,
  aiWorks,
  newClientBrand,
} from './seed';
import type {
  Member,
  NoticeChannel,
  NoticeEvent,
  StaffMember,
  Account,
  Asset,
  Brand,
  BrandSettings,
  Campaign,
  Conversation,
  DriveLink,
  Message,
  Notice,
  Task,
  Template,
  Upload,
  WorkspaceId,
  WorkspaceSettings,
  Business,
  AiConnection,
  AiScenario,
  HelperInvite,
  Onboarding,
  SetupStep,
  PersonalPreferences,
  AgencyRequest,
} from './seed';
import type { Channel } from '../types';
import { usageRows } from './setup';
import { emptyDraft } from './welcome';
import type { WelcomeDraft } from './welcome';
import {
  looksPolish,
  buildPlan,
  dayFrom,
  draftPost,
  freeDay,
  guessChannel,
  guessKind,
  suggestIdeas,
  titleFrom,
} from './campaigns';

export interface DemoState {
  posts: Post[];
  accounts: Account[];
  assets: Asset[];
  conversations: Conversation[];
  brands: Brand[];
  settings: WorkspaceSettings[];
  notices: Notice[];
  revising: { postId: string; readyAt: number; note: string; image?: string }[];
  reminders: Record<string, string>;
  uploads: Upload[];
  drive: DriveLink[];
  campaigns: Campaign[];
  tasks: Task[];
  templates: Template[];
  members: Member[];
  staff: StaffMember[];
  business: Business[];
  aiConnections: AiConnection[];
  helpers: HelperInvite[];
  onboarding: Onboarding[];
  requests: AgencyRequest[];
  dailyLimitOverrides: Record<string, Partial<Record<Channel, number>>>;
  welcome: Record<string, WelcomeDraft>;
}

const STORAGE_KEY = 'amy-demo-state-v12';
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
function runIgnoringUnavailableStorage(storageAction: () => void) {
  try {
    storageAction();
  } catch {
    return;
  }
}
function seedState(): DemoState {
  return clone({
    posts: seedPosts,
    accounts: seedAccounts,
    assets: seedAssets,
    conversations: seedConversations,
    brands: seedBrands,
    settings: seedSettings,
    notices: seedNotices,
    revising: [],
    reminders: {},
    uploads: seedUploads,
    drive: seedDrive,
    campaigns: seedCampaigns,
    tasks: seedTasks,
    templates: seedTemplates,
    members: seedMembers,
    staff: seedStaff,
    business: seedBusiness,
    aiConnections: seedAi,
    helpers: [],
    onboarding: seedOnboarding,
    requests: [],
    dailyLimitOverrides: {},
    welcome: {},
  });
}
function loadSavedStateOrStartFresh(): DemoState {
  try {
    const savedJson = window.localStorage.getItem(STORAGE_KEY);
    if (savedJson) return { ...seedState(), ...JSON.parse(savedJson) };
  } catch {
    return seedState();
  }
  return seedState();
}

const FIRST_DRAFT = 'First draft from the accepted idea.';
const SEEDED_UPLOAD_PREPARATION_MILLISECONDS = 20000;
let messageCounter = 0;
const nowLabel = () =>
  new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const dayLabelNow = () => `${DEMO_TODAY} Nov`;
const nextMessageId = () => `m-${Date.now()}-${messageCounter++}`;
const LAST_DAY_OF_NOVEMBER = 30;
const demoDayLabel = (dayNumber: number) =>
  dayNumber <= LAST_DAY_OF_NOVEMBER
    ? `${dayNumber} Nov`
    : `${dayNumber - LAST_DAY_OF_NOVEMBER} Dec`;

export interface Actor {
  name: string;
  role: 'owner' | 'contributor' | 'agency' | 'admin';
}

const STATUSES_THAT_RELEASE_THE_DAILY_SLOT: PostStatus[] = [
  'Approved · Stopped',
  'Rejected',
  'Intentionally empty',
  'Cancelled',
];
const STATUSES_THAT_NEED_APPROVAL_AGAIN_ON_NEW_ACCOUNT: PostStatus[] = [
  'Scheduled',
  'Approved · Stopped',
];
export const postStatusHoldsDailySlot = (status: PostStatus) =>
  !STATUSES_THAT_RELEASE_THE_DAILY_SLOT.includes(status);

function completeTask(state: DemoState, task: Task): DemoState {
  const campaign = state.campaigns.find((item) => item.id === task.campaignId);
  const amyMessage = (text: string, vars?: Record<string, string>, postId?: string): Message => ({
    id: nextMessageId(),
    from: 'amy',
    at: nowLabel(),
    text,
    variables: vars,
    postId,
    taskId: task.id,
  });
  const targetConversationId =
    task.conversationId ??
    campaign?.conversationId ??
    state.conversations.find(
      (conversation) =>
        conversation.workspaceId === task.workspaceId && conversation.scope === 'general'
    )?.id;
  const addMessage = (stateToUpdate: DemoState, message: Message) => ({
    ...stateToUpdate,
    conversations: stateToUpdate.conversations.map((conversation) =>
      conversation.id === targetConversationId
        ? {
            ...conversation,
            messages: [...conversation.messages, message],
            unread: conversation.unread + 1,
          }
        : conversation
    ),
  });
  const finishTaskWith = (stateToUpdate: DemoState, patch: Partial<Task>) => ({
    ...stateToUpdate,
    tasks: stateToUpdate.tasks.map((existingTask) =>
      existingTask.id === task.id ? { ...existingTask, ...patch } : existingTask
    ),
  });

  if (task.kind === 'ideas' && campaign) {
    const ideas = suggestIdeas(campaign, 4);
    let stateWithIdeas: DemoState = {
      ...state,
      campaigns: state.campaigns.map((existingCampaign) =>
        existingCampaign.id === campaign.id
          ? {
              ...existingCampaign,
              ideas: [...existingCampaign.ideas, ...ideas],
              status: ['Draft', 'Planning', 'Needs your input'].includes(existingCampaign.status)
                ? ('Ideas ready' as const)
                : existingCampaign.status,
            }
          : existingCampaign
      ),
    };
    stateWithIdeas = finishTaskWith(stateWithIdeas, {
      status: 'Ready',
      result: {
        text: 'Suggested {n} ideas for “{name}”.',
        variables: { n: String(ideas.length), name: campaign.name },
        ideaCount: ideas.length,
      },
    });
    stateWithIdeas = addMessage(
      stateWithIdeas,
      amyMessage(
        'I have {n} new ideas for “{name}”. Accept the ones you like and I will put them in the calendar.',
        { n: String(ideas.length), name: campaign.name }
      )
    );
    return {
      ...stateWithIdeas,
      notices: [
        ...stateWithIdeas.notices,
        {
          id: `n-${task.id}`,
          workspaceId: task.workspaceId,
          at: nowLabel(),
          text: '{name}: {n} new ideas',
          variables: { name: campaign.name, n: String(ideas.length) },
          link: `ideas/${campaign.id}`,
          read: false,
        },
      ],
    };
  }
  if (task.kind === 'plan' && campaign) {
    const plan = buildPlan(campaign, state.posts);
    let stateWithPlan: DemoState = {
      ...state,
      campaigns: state.campaigns.map((existingCampaign) =>
        existingCampaign.id === campaign.id
          ? { ...existingCampaign, plan, question: undefined, status: 'Plan ready' as const }
          : existingCampaign
      ),
    };
    stateWithPlan = finishTaskWith(stateWithPlan, {
      status: 'Ready',
      result: {
        text: 'Plan ready: {n} posts for “{name}”.',
        variables: { n: String(plan.length), name: campaign.name },
      },
    });
    stateWithPlan = addMessage(
      stateWithPlan,
      amyMessage(
        'The plan for “{name}” is ready: {n} posts. Review it and activate the campaign when it looks right.',
        { n: String(plan.length), name: campaign.name }
      )
    );
    return {
      ...stateWithPlan,
      notices: [
        ...stateWithPlan.notices,
        {
          id: `n-${task.id}`,
          workspaceId: task.workspaceId,
          at: nowLabel(),
          text: '{name}: plan ready for review',
          variables: { name: campaign.name },
          link: `ideas/${campaign.id}`,
          read: false,
        },
      ],
    };
  }
  if (task.kind === 'post') {
    const channels = task.channels?.length ? task.channels : [guessChannel(task.title, campaign)];
    const disconnectedChannels = channels.filter(
      (channel) =>
        state.accounts.find(
          (account) =>
            account.workspaceId === task.workspaceId &&
            account.network === channel &&
            !/personal/i.test(account.type)
        )?.health !== 'Connected'
    );
    const connectedChannels = channels.filter((channel) => !disconnectedChannels.includes(channel));
    if (!connectedChannels.length) {
      const channel = disconnectedChannels[0];
      const accountExistsButNeedsReconnecting = state.accounts.some(
        (account) => account.workspaceId === task.workspaceId && account.network === channel
      );
      const stateWithFailedTask = finishTaskWith(state, {
        status: 'Failed',
        reason: accountExistsButNeedsReconnecting
          ? '{channel} needs reconnecting, so AMY could not prepare a {channel} version.'
          : '{channel} is not connected for this workspace.',
        result: { text: '', variables: { channel } },
      });
      return addMessage(
        stateWithFailedTask,
        amyMessage(
          'I could not finish “{title}”: {channel} needs reconnecting. Reconnect it in Settings and press Retry on the task.',
          { title: titleFrom(task.title), channel }
        )
      );
    }
    const wantedDay = task.day ?? task.plannedFor ?? campaign?.start ?? DEMO_TODAY + 1;
    const attachedMaterial = task.materials
      ?.map((name) =>
        state.assets.find(
          (asset) =>
            asset.workspaceId === task.workspaceId && (asset.name === name || asset.file === name)
        )
      )
      .find((asset) => asset?.image);
    const draftedPosts: Post[] = [];
    const missed: string[] = [];
    const channelsFullUntilAfterEvent: { channel: string; next?: number }[] = [];
    for (const channel of connectedChannels) {
      const isSlotFreeOn = (candidateDay: number) =>
        candidateDay > DEMO_TODAY &&
        ![...state.posts, ...draftedPosts].some(
          (existingPost) =>
            existingPost.workspaceId === task.workspaceId &&
            existingPost.channel === channel &&
            existingPost.day === candidateDay &&
            postStatusHoldsDailySlot(existingPost.status)
        );
      const eventDayOrUpToThreeDaysBefore = task.day
        ? [task.day, task.day - 1, task.day - 2, task.day - 3].find(isSlotFreeOn)
        : undefined;
      const firstFreeDayFromWantedDay = () =>
        freeDay(
          [...state.posts, ...draftedPosts],
          task.workspaceId,
          channel,
          wantedDay,
          Math.max(wantedDay, campaign?.end ?? 30) + 7
        );
      const day = task.day
        ? (eventDayOrUpToThreeDaysBefore ??
          (task.allowAfter ? firstFreeDayFromWantedDay() : undefined))
        : firstFreeDayFromWantedDay();
      const eventDayFullAndClientMustChoose =
        task.day && !eventDayOrUpToThreeDaysBefore && !task.allowAfter && !task.skipLate;
      if (eventDayFullAndClientMustChoose) {
        channelsFullUntilAfterEvent.push({ channel, next: firstFreeDayFromWantedDay() });
        continue;
      }
      if (!day) {
        missed.push(channel);
        continue;
      }
      const post = draftPost({
        workspaceId: task.workspaceId,
        title: titleFrom(task.title),
        channel,
        format: channel === 'TikTok' ? 'Reel' : 'Photo',
        day,
        campaign,
        accounts: state.accounts,
        assets: state.assets,
        brief: task.title,
        language: campaign ? undefined : looksPolish(task.title) ? 'pl' : 'en',
      });
      draftedPosts.push({
        ...post,
        id: `${post.id}-${draftedPosts.length}`,
        requestId: task.id,
        groupId: task.id,
        materials: task.materials,
        image: attachedMaterial?.image ?? post.image,
        history: [
          {
            at: nowLabel(),
            by: 'AMY',
            text: 'Drafted from {name}’s request',
            variables: { name: task.by },
          },
        ],
      });
    }
    if (channelsFullUntilAfterEvent.length) {
      const dayLabel = (dayNumber?: number) => (dayNumber ? demoDayLabel(dayNumber) : '—');
      const fullChannelNames = channelsFullUntilAfterEvent
        .map((lateChannel) => lateChannel.channel)
        .join(', ');
      const nextFreeDayPerChannel = channelsFullUntilAfterEvent
        .map((lateChannel) => `${lateChannel.channel} ${dayLabel(lateChannel.next)}`)
        .join(', ');
      const stateWaitingForChoice = finishTaskWith(state, {
        status: 'Waiting',
        question:
          '{asked} and the three days before are full on {channels}. Next free: {next}. Post then, or only on the channels that fit?',
        questionVars: {
          asked: dayLabel(task.day),
          channels: fullChannelNames,
          next: nextFreeDayPerChannel,
        },
        choices: [
          { label: 'Use the next free days', value: 'allowAfter' },
          ...(connectedChannels.length > channelsFullUntilAfterEvent.length
            ? [{ label: 'Only the channels that fit', value: 'skipLate' }]
            : []),
        ],
        nextStep: 'Choose below, or pick another date with Add an idea.',
      });
      return addMessage(
        stateWaitingForChoice,
        amyMessage(
          '{asked} is full on {channels}. Should I use the next free day ({next}) or skip those channels?',
          {
            asked: dayLabel(task.day),
            channels: fullChannelNames,
            next: nextFreeDayPerChannel,
          }
        )
      );
    }
    if (!draftedPosts.length) {
      const askedLabel = task.day ? demoDayLabel(task.day) : '';
      const stateWithFailedTask = finishTaskWith(state, {
        status: 'Failed',
        reason: task.day
          ? 'No free {channel} slot on {asked} or the three days before. Free a slot and press Retry.'
          : 'No free {channel} slot in the next weeks. Free a slot or allow more posts per day.',
        result: { text: '', variables: { channel: missed.join(', '), asked: askedLabel } },
      });
      return addMessage(
        stateWithFailedTask,
        amyMessage(
          'There is no free {channel} slot for “{title}”. Free a slot in the calendar and press Retry.',
          { title: titleFrom(task.title), channel: missed[0] }
        )
      );
    }
    let stateWithDrafts: DemoState = { ...state, posts: [...state.posts, ...draftedPosts] };
    const skipped = [...disconnectedChannels, ...missed];
    const where = draftedPosts
      .map((madePost) => `${madePost.channel} ${demoDayLabel(madePost.day)}`)
      .join(', ');
    const postsNotOnAskedDay = task.day
      ? draftedPosts.filter((madePost) => madePost.day !== task.day)
      : [];
    const postsAfterAskedDay = task.day
      ? draftedPosts.filter((madePost) => madePost.day > task.day!)
      : [];
    const skippedReason = skipped.length
      ? task.day && missed.length
        ? 'Not made for {channels}: no free slot on or up to three days before {asked}. Free a slot, then ask again.'
        : 'Not made for {channels}: reconnect the account or free a slot, then ask again.'
      : undefined;
    const resultText = postsAfterAskedDay.length
      ? 'As you chose, {full} goes out after {asked}: {where}. Use Move on a draft to change it.'
      : postsNotOnAskedDay.length
        ? '{asked} was already full on {full}, so AMY used an earlier free day: {where}. Use Move on a draft to change it.'
        : draftedPosts.length > 1
          ? 'Drafts ready: {where}.'
          : 'Draft ready for {day}.';
    stateWithDrafts = finishTaskWith(stateWithDrafts, {
      status: 'Ready',
      reason: skippedReason,
      result: {
        text: resultText,
        variables: {
          n: String(draftedPosts.length),
          day: demoDayLabel(draftedPosts[0].day),
          where,
          asked: task.day ? demoDayLabel(task.day) : '',
          full: postsNotOnAskedDay.map((shiftedPost) => shiftedPost.channel).join(', '),
          channels: skipped.join(', '),
        },
        postIds: draftedPosts.map((madePost) => madePost.id),
      },
    });
    stateWithDrafts = addMessage(
      stateWithDrafts,
      amyMessage(
        'Here is a draft for “{title}” on {channel}. Have a look when you have a moment.',
        {
          title: draftedPosts[0].title,
          channel: draftedPosts.map((madePost) => madePost.channel).join(', '),
        },
        draftedPosts[0].id
      )
    );
    return {
      ...stateWithDrafts,
      notices: [
        ...stateWithDrafts.notices,
        {
          id: `n-${task.id}`,
          workspaceId: task.workspaceId,
          at: nowLabel(),
          text: '{title}: first draft ready for review',
          variables: { title: draftedPosts[0].title },
          link: `post/${draftedPosts[0].id}`,
          read: false,
        },
      ],
    };
  }
  const stateWithNotedTask = finishTaskWith(state, {
    status: 'Ready',
    result: { text: 'Noted. AMY will use this for new content.' },
  });
  return addMessage(
    stateWithNotedTask,
    amyMessage('Noted. I will keep this in mind for new content.')
  );
}

const SAMPLE_POST_HISTORY_TEXT = 'Sample post from the campaign plan';

function areAllSamplePostsApproved(campaign: Campaign, posts: Post[]) {
  const samplePosts = posts.filter(
    (post) =>
      post.campaign === campaign.key &&
      post.workspaceId === campaign.workspaceId &&
      post.history[0]?.text === SAMPLE_POST_HISTORY_TEXT
  );
  return (
    samplePosts.length > 0 &&
    samplePosts.every(
      (samplePost) => samplePost.status === 'Scheduled' || samplePost.status === 'Published'
    )
  );
}

function websiteWithProtocol(website: string) {
  if (/^https?:\/\//.test(website)) return website;
  return website ? `https://${website}` : '';
}

const NEW_CLIENT_WORKSPACE_ID = 'nowa';

function workspaceShowingNewClientBusinessName(workspaceId: string, businesses: Business[]) {
  const workspace = workspaces.find((item) => item.id === workspaceId) ?? workspaces[0];
  const business = businesses.find(
    (candidateBusiness) => candidateBusiness.workspaceId === workspace.id
  );
  return workspace.id === NEW_CLIENT_WORKSPACE_ID && business?.name
    ? { ...workspace, name: business.name }
    : workspace;
}

export function useDemoStoreValue() {
  const [state, setState] = useState<DemoState>(loadSavedStateOrStartFresh);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    runIgnoringUnavailableStorage(() =>
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    );
  }, [state]);

  useEffect(() => {
    const isAiWorkingForPostWorkspace = (demoState: DemoState, postId: string) => {
      const postWorkspaceId = demoState.posts.find((post) => post.id === postId)?.workspaceId;
      return aiWorks(
        demoState.aiConnections.find((aiConnection) => aiConnection.workspaceId === postWorkspaceId)
      );
    };
    const latestState = stateRef.current;
    const revisionsWithWorkingAi = state.revising.filter((item) =>
      isAiWorkingForPostWorkspace(latestState, item.postId)
    );
    if (!revisionsWithWorkingAi.length) return;
    const millisecondsUntilNextRevision =
      Math.min(...revisionsWithWorkingAi.map((item) => item.readyAt)) - Date.now();
    const revisionTimeoutId = window.setTimeout(
      () => {
        setState((current) => {
          const dueRevisions = current.revising.filter(
            (item) =>
              item.readyAt <= Date.now() && isAiWorkingForPostWorkspace(current, item.postId)
          );
          if (!dueRevisions.length) return current;
          const posts = current.posts.map((post) => {
            const job = dueRevisions.find((item) => item.postId === post.id);
            if (!job) return post;
            if (job.note === FIRST_DRAFT)
              return {
                ...post,
                status: 'Needs your review' as PostStatus,
                history: [
                  ...post.history,
                  { at: nowLabel(), by: 'AMY', text: 'First draft ready' },
                ],
              };
            return {
              ...post,
              status: 'Needs your review' as PostStatus,
              revision: post.revision + 1,
              previousCaption: post.caption,
              changeNote: job.note,
              versions: [
                ...(post.versions ?? []),
                {
                  revision: post.revision,
                  caption: post.caption,
                  image: post.image,
                  note: post.changeNote,
                },
              ],
              ...(job.image ? { previousImage: post.image, image: job.image } : {}),
              history: [
                ...post.history,
                {
                  at: nowLabel(),
                  by: 'AMY',
                  text: 'Version {n} ready',
                  variables: { n: String(post.revision + 1) },
                },
              ],
            };
          });
          const notices = [
            ...current.notices,
            ...dueRevisions.map((job) => {
              const post = current.posts.find((item) => item.id === job.postId)!;
              return {
                id: `n-${job.postId}-${Date.now()}`,
                workspaceId: post.workspaceId as WorkspaceId,
                at: nowLabel(),
                text:
                  job.note === FIRST_DRAFT
                    ? '{title}: first draft ready for review'
                    : '{title}: new version ready for review',
                variables: { title: post.title },
                link: `post/${post.id}`,
                read: false,
              };
            }),
          ];
          const conversations = current.conversations.map((conversation) => {
            const job = dueRevisions.find(
              (item) =>
                conversation.scope === 'general' &&
                current.posts.find((post) => post.id === item.postId)?.workspaceId ===
                  conversation.workspaceId
            );
            if (!job) return conversation;
            return {
              ...conversation,
              messages: [
                ...conversation.messages,
                {
                  id: nextMessageId(),
                  from: 'amy' as const,
                  at: nowLabel(),
                  text:
                    job.note === FIRST_DRAFT
                      ? 'The first draft from your accepted idea is ready for review.'
                      : 'The new version is ready. Have a look when you have a moment.',
                  postId: job.postId,
                },
              ],
            };
          });
          return {
            ...current,
            posts,
            notices,
            conversations,
            revising: current.revising.filter((item) => !dueRevisions.includes(item)),
          };
        });
      },
      Math.max(millisecondsUntilNextRevision, 50)
    );
    return () => window.clearTimeout(revisionTimeoutId);
  }, [state.revising, state.aiConnections]);

  const hasTasksToStartOrFinish = state.tasks.some(
    (task) => (task.status === 'Queued' && task.startAt) || task.status === 'Working'
  );
  useEffect(() => {
    if (!hasTasksToStartOrFinish) return;
    const taskTickIntervalId = window.setInterval(
      () =>
        setState((current) => {
          const now = Date.now();
          let changed = false;
          let nextState = current;
          const isAiWorkingForWorkspace = (workspaceId: string) =>
            aiWorks(
              current.aiConnections.find((aiConnection) => aiConnection.workspaceId === workspaceId)
            );
          const tasks = current.tasks.map((task) => {
            if (
              task.status === 'Queued' &&
              task.startAt &&
              task.startAt <= now &&
              isAiWorkingForWorkspace(task.workspaceId)
            ) {
              changed = true;
              return { ...task, status: 'Working' as const };
            }
            return task;
          });
          nextState = { ...nextState, tasks };
          for (const task of tasks) {
            if (
              task.status !== 'Working' ||
              !task.readyAt ||
              task.readyAt > now ||
              !isAiWorkingForWorkspace(task.workspaceId)
            )
              continue;
            changed = true;
            nextState = completeTask(nextState, task);
          }
          return changed ? nextState : current;
        }),
      400
    );
    return () => window.clearInterval(taskTickIntervalId);
  }, [hasTasksToStartOrFinish]);

  const hasUploadsOrMediaInProgress =
    state.uploads.some((upload) => upload.state === 'uploading' || upload.state === 'preparing') ||
    state.assets.some((asset) => asset.preparingUntil);
  useEffect(() => {
    if (!hasUploadsOrMediaInProgress) return;
    const uploadTickIntervalId = window.setInterval(
      () =>
        setState((current) => {
          const now = Date.now();
          const newAssets: Asset[] = [];
          const uploads = current.uploads.map((upload) => {
            if (upload.state === 'uploading') {
              const progressStep = Math.max(2, Math.round(400 / Math.max(upload.sizeMb, 4)));
              const progress = Math.min(100, upload.progress + progressStep);
              return progress >= 100
                ? {
                    ...upload,
                    progress: 100,
                    state: 'preparing' as const,
                    readyAt: now + 2500 + Math.min(upload.sizeMb, 2000),
                  }
                : { ...upload, progress };
            }
            if (upload.state === 'preparing') {
              if (!upload.readyAt)
                return { ...upload, readyAt: now + SEEDED_UPLOAD_PREPARATION_MILLISECONDS };
              if (upload.readyAt <= now) {
                newAssets.push({
                  id: `asset-${upload.id}`,
                  workspaceId: upload.workspaceId,
                  name: upload.name.replace(/\.[^.]+$/, ''),
                  kind: upload.kind,
                  file: upload.name,
                  size:
                    upload.sizeMb >= 1000
                      ? `${(upload.sizeMb / 1000).toFixed(1)} GB`
                      : `${upload.sizeMb} MB`,
                  image: upload.src ?? '',
                  badges: [],
                  source: 'Uploaded',
                  added: nowLabel(),
                  tags: [],
                });
                return { ...upload, state: 'done' as const };
              }
            }
            return upload;
          });
          const assets = [
            ...current.assets.map((asset) =>
              asset.preparingUntil && asset.preparingUntil <= now
                ? {
                    ...asset,
                    preparingUntil: undefined,
                    badges: asset.badges.filter((badge) => badge !== 'Preparing media'),
                  }
                : asset
            ),
            ...newAssets,
          ];
          return { ...current, uploads, assets };
        }),
      500
    );
    return () => window.clearInterval(uploadTickIntervalId);
  }, [hasUploadsOrMediaInProgress]);

  const updatePost = useCallback((id: string, change: (post: Post) => Partial<Post>) => {
    setState((current) => ({
      ...current,
      posts: current.posts.map((post) => (post.id === id ? { ...post, ...change(post) } : post)),
    }));
  }, []);

  const logToChat = useCallback(
    (workspaceId: string, text: string, vars?: Record<string, string>, postId?: string) => {
      setState((current) => ({
        ...current,
        conversations: current.conversations.map((conversation) =>
          conversation.workspaceId === workspaceId && conversation.scope === 'general'
            ? {
                ...conversation,
                messages: [
                  ...conversation.messages,
                  {
                    id: nextMessageId(),
                    from: 'system' as const,
                    at: nowLabel(),
                    text,
                    variables: vars,
                    postId,
                  },
                ],
              }
            : conversation
        ),
      }));
    },
    []
  );

  const draftSlots = useCallback(
    (
      campaign: Campaign,
      wantedSlots: { day: number; channel: Channel; format: Format; title: string }[],
      actor: Actor | 'AMY',
      options: { samples: boolean }
    ) => {
      const latestState = stateRef.current;
      const slotsStillFree = wantedSlots.filter(
        (slot) =>
          !latestState.posts.some(
            (existingPost) =>
              existingPost.workspaceId === campaign.workspaceId &&
              existingPost.channel === slot.channel &&
              existingPost.day === slot.day &&
              postStatusHoldsDailySlot(existingPost.status)
          )
      );
      const clashingSlotCount = wantedSlots.length - slotsStillFree.length;
      if (clashingSlotCount)
        logToChat(
          campaign.workspaceId,
          '{n} planned posts for “{title}” clash with posts already in the calendar. Edit their day in the plan.',
          { n: String(clashingSlotCount), title: campaign.name }
        );
      if (!slotsStillFree.length) return 0;
      const draftedPosts = slotsStillFree.map((slot, slotIndex) => ({
        ...draftPost({
          workspaceId: campaign.workspaceId,
          title: slot.title,
          channel: slot.channel,
          format: slot.format,
          day: slot.day,
          campaign,
          accounts: latestState.accounts,
          assets: latestState.assets,
          status: 'AMY is working',
        }),
        id: `${campaign.workspaceId === 'lipa' ? 'lip' : 'brz'}-b${Date.now()}-${slotIndex}`,
        history: [
          {
            at: nowLabel(),
            by: actor === 'AMY' ? 'AMY' : actor.name,
            text: options.samples ? SAMPLE_POST_HISTORY_TEXT : 'Drafted in an automatic batch',
          },
        ],
      }));
      const lastDraftedDay = Math.max(...draftedPosts.map((madePost) => madePost.day));
      setState((current) => ({
        ...current,
        posts: [...current.posts, ...draftedPosts],
        campaigns: current.campaigns.map((existingCampaign) =>
          existingCampaign.id === campaign.id
            ? {
                ...existingCampaign,
                status: 'Active' as const,
                samplesApproved: options.samples ? false : existingCampaign.samplesApproved,
                nextBatch: lastDraftedDay + 1,
                plan: existingCampaign.plan?.map((slot) => {
                  const draftedPostForSlot = draftedPosts.find(
                    (madePost) =>
                      madePost.day === slot.day &&
                      madePost.channel === slot.channel &&
                      madePost.title === slot.title
                  );
                  return draftedPostForSlot ? { ...slot, postId: draftedPostForSlot.id } : slot;
                }),
              }
            : existingCampaign
        ),
        revising: [
          ...current.revising,
          ...draftedPosts.map((madePost, draftIndex) => ({
            postId: madePost.id,
            readyAt: Date.now() + 3500 + draftIndex * 700,
            note: FIRST_DRAFT,
          })),
        ],
      }));
      logToChat(
        campaign.workspaceId,
        options.samples
          ? 'AMY drafted {n} sample posts for “{title}”. Approve them and AMY drafts the rest in batches.'
          : actor === 'AMY'
            ? 'Samples approved. AMY drafted the next {n} posts for “{title}” automatically.'
            : 'AMY drafted the next {n} posts for “{title}”.',
        { n: String(draftedPosts.length), title: campaign.name }
      );
      return draftedPosts.length;
    },
    [logToChat]
  );

  const commands = useMemo(
    () => ({
      approve(id: string, actor: Actor) {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post || post.status !== 'Needs your review') return false;
        const isApprovedAfterItsTime =
          post.day < DEMO_TODAY || (post.day === DEMO_TODAY && post.time < DEMO_NOW);
        if (isApprovedAfterItsTime) {
          const campaign = stateRef.current.campaigns.find(
            (candidateCampaign) =>
              candidateCampaign.key === post.campaign &&
              candidateCampaign.workspaceId === post.workspaceId
          );
          const nextFreeDayInCampaign = freeDay(
            stateRef.current.posts.filter((otherPost) => otherPost.id !== id),
            post.workspaceId,
            post.channel,
            DEMO_TODAY + 1,
            Math.max(campaign?.end ?? DEMO_TODAY + 7, DEMO_TODAY + 1)
          );
          if (nextFreeDayInCampaign)
            updatePost(id, (postToUpdate) => ({
              status: 'Scheduled',
              day: nextFreeDayInCampaign,
              history: [
                ...postToUpdate.history,
                {
                  at: nowLabel(),
                  by: actor.name,
                  text: 'Approved after its time; moved to the next free slot: {when}',
                  variables: {
                    when: `${demoDayLabel(nextFreeDayInCampaign)} ${postToUpdate.time}`,
                  },
                },
              ],
            }));
          else
            updatePost(id, (postToUpdate) => ({
              status: 'Approved · Stopped',
              needsNewTime: true,
              history: [
                ...postToUpdate.history,
                {
                  at: nowLabel(),
                  by: actor.name,
                  text: 'Approved after its time; no free slot, needs a new time',
                },
              ],
            }));
          logToChat(
            post.workspaceId,
            nextFreeDayInCampaign
              ? '{name} approved “{title}” after its time. It moved to {when}.'
              : '{name} approved “{title}” after its time. It needs a new time.',
            {
              name: actor.name,
              title: post.title,
              when: nextFreeDayInCampaign ? demoDayLabel(nextFreeDayInCampaign) : '',
            },
            id
          );
          return true;
        }
        updatePost(id, (postToUpdate) => ({
          status: 'Scheduled',
          history: [
            ...postToUpdate.history,
            { at: nowLabel(), by: actor.name, text: 'Approved and scheduled' },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} approved “{title}” ({channel}). It is scheduled.',
          { name: actor.name, title: post.title, channel: post.channel },
          id
        );
        return true;
      },
      requestChanges(id: string, text: string, actor: Actor, image?: string) {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post) return;
        const aiReady = aiWorks(
          stateRef.current.aiConnections.find(
            (aiConnection) => aiConnection.workspaceId === post.workspaceId
          )
        );
        updatePost(id, (postToUpdate) => ({
          status: 'AMY is working',
          history: [
            ...postToUpdate.history,
            {
              at: nowLabel(),
              by: actor.name,
              text: 'Asked for changes: {text}',
              variables: { text },
            },
            ...(aiReady
              ? []
              : [
                  {
                    at: nowLabel(),
                    by: 'AMY',
                    text: 'Waiting for an AI connection before making the new version',
                  },
                ]),
          ],
        }));
        setState((current) => ({
          ...current,
          revising: [
            ...current.revising,
            { postId: id, readyAt: Date.now() + 6000, note: text, image },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} asked for changes to “{title}”: {text}',
          { name: actor.name, title: post.title, text },
          id
        );
      },
      reject(id: string, decision: 'replace' | 'empty' | 'later', actor: Actor) {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post) return;
        const rejectionHistoryText =
          decision === 'replace'
            ? 'Rejected · AMY prepares a different idea'
            : decision === 'empty'
              ? 'Rejected · slot left empty'
              : 'Rejected · decide later';
        updatePost(id, (postToUpdate) => ({
          status: decision === 'empty' ? 'Intentionally empty' : 'Rejected',
          history: [
            ...postToUpdate.history,
            { at: nowLabel(), by: actor.name, text: rejectionHistoryText },
          ],
        }));
        if (decision === 'replace') {
          const replacement: Post = {
            ...post,
            id: `${post.id}-r`,
            title:
              post.language === 'pl'
                ? `Nowy pomysł: ${post.channel}`
                : `New idea for ${post.channel}`,
            status: 'AMY is working',
            revision: 1,
            changeNote: undefined,
            previousCaption: undefined,
            history: [
              {
                at: nowLabel(),
                by: 'AMY',
                text: 'Replacement for “{title}”',
                variables: { title: post.title },
              },
            ],
            comments: [],
          };
          setState((current) => ({
            ...current,
            revising: [
              ...current.revising,
              {
                postId: replacement.id,
                readyAt: Date.now() + 8000,
                note: 'A new idea for this slot.',
              },
            ],
          }));
          setState((current) => ({ ...current, posts: [...current.posts, replacement] }));
        }
        logToChat(
          post.workspaceId,
          decision === 'replace'
            ? '{name} rejected “{title}”. AMY is preparing a different idea for the same slot.'
            : decision === 'empty'
              ? '{name} rejected “{title}”. The slot stays empty.'
              : '{name} rejected “{title}”. The slot is on hold until someone decides.',
          { name: actor.name, title: post.title },
          id
        );
      },
      stop(id: string, actor: Actor) {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post || post.status !== 'Scheduled') return;
        updatePost(id, (postToUpdate) => ({
          status: 'Approved · Stopped',
          history: [
            ...postToUpdate.history,
            { at: nowLabel(), by: actor.name, text: 'Stopped before publishing' },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} stopped “{title}” ({channel}). It stays on hold until someone resumes or reschedules it.',
          { name: actor.name, title: post.title, channel: post.channel },
          id
        );
      },
      resume(id: string, actor: Actor): 'ok' | 'taken' | 'invalid' {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post || post.status !== 'Approved · Stopped' || post.needsNewTime) return 'invalid';
        const taken = stateRef.current.posts.some(
          (other) =>
            other.id !== id &&
            other.workspaceId === post.workspaceId &&
            other.channel === post.channel &&
            other.day === post.day &&
            postStatusHoldsDailySlot(other.status)
        );
        if (taken) return 'taken';
        updatePost(id, (postToUpdate) => ({
          status: 'Scheduled',
          history: [
            ...postToUpdate.history,
            { at: nowLabel(), by: actor.name, text: 'Resumed at the original time' },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} resumed “{title}”.',
          { name: actor.name, title: post.title },
          id
        );
        return 'ok';
      },
      comment(id: string, text: string, actor: Actor) {
        updatePost(id, (postToUpdate) => ({
          comments: [
            ...postToUpdate.comments,
            { at: nowLabel(), by: actor.name, text, showAsWritten: true },
          ],
        }));
      },
      setNoLate(id: string, value: boolean) {
        updatePost(id, () => ({ neverPublishLate: value }));
      },
      resumePublishing(workspaceId: string, resumeAt: string, actor: Actor) {
        const minutesSinceMidnight = (clockTime: string) => {
          const [hours, minutes] = clockTime.split(':').map(Number);
          return hours * 60 + minutes;
        };
        const missed = stateRef.current.posts.filter(
          (scheduledPost) =>
            scheduledPost.workspaceId === workspaceId &&
            scheduledPost.status === 'Scheduled' &&
            scheduledPost.day === DEMO_TODAY &&
            scheduledPost.time >= DEMO_NOW &&
            scheduledPost.time < resumeAt
        );
        const at = nowLabel();
        const outcome = missed.map((missedPost) => ({
          id: missedPost.id,
          late: minutesSinceMidnight(resumeAt) - minutesSinceMidnight(missedPost.time),
          canPublishLate:
            !missedPost.neverPublishLate &&
            minutesSinceMidnight(resumeAt) - minutesSinceMidnight(missedPost.time) <=
              LATE_WINDOW_MINUTES,
        }));
        setState((current) => ({
          ...current,
          settings: current.settings.map((item) =>
            item.workspaceId === workspaceId
              ? { ...item, paused: false, pausedBy: undefined }
              : item
          ),
          posts: current.posts.map((existingPost) => {
            const postOutcome = outcome.find((outcomeEntry) => outcomeEntry.id === existingPost.id);
            if (!postOutcome) return existingPost;
            return postOutcome.canPublishLate
              ? {
                  ...existingPost,
                  status: 'Published' as const,
                  results: [{ account: existingPost.account, succeeded: true, at: resumeAt }],
                  history: [
                    ...existingPost.history,
                    {
                      at,
                      by: 'AMY',
                      text: 'Published {m} minutes late after the pause',
                      variables: { m: String(postOutcome.late) },
                    },
                  ],
                }
              : {
                  ...existingPost,
                  status: 'Approved · Stopped' as const,
                  needsNewTime: true,
                  history: [
                    ...existingPost.history,
                    {
                      at,
                      by: 'AMY',
                      text: existingPost.neverPublishLate
                        ? 'Missed during the pause; marked do not publish late'
                        : 'Missed during the pause by more than two hours',
                    },
                  ],
                };
          }),
        }));
        logToChat(workspaceId, '{name} resumed publishing at {time}.', {
          name: actor.name,
          time: resumeAt,
        });
        return outcome;
      },
      setPaused(workspaceId: string, paused: boolean, actor: Actor) {
        setState((current) => ({
          ...current,
          settings: current.settings.map((item) =>
            item.workspaceId === workspaceId
              ? { ...item, paused, pausedBy: paused ? actor.name : undefined }
              : item
          ),
        }));
        logToChat(
          workspaceId,
          paused ? '{name} paused all publishing.' : '{name} resumed publishing.',
          { name: actor.name }
        );
      },
      setPermission(
        workspaceId: string,
        key: 'contributorCanApprove' | 'contributorCanStop',
        value: boolean
      ) {
        setState((current) => ({
          ...current,
          settings: current.settings.map((item) =>
            item.workspaceId === workspaceId ? { ...item, [key]: value } : item
          ),
        }));
      },
      movePost(
        id: string,
        day: number,
        time: string,
        actor: Actor
      ): 'ok' | 'taken' | 'past' | 'locked' {
        const latestState = stateRef.current;
        const post = latestState.posts.find((item) => item.id === id);
        if (
          !post ||
          ['Published', 'Rejected', 'Cancelled', 'Intentionally empty'].includes(post.status)
        )
          return 'locked';
        if (day <= DEMO_TODAY) return 'past';
        if (
          latestState.posts.some(
            (otherPost) =>
              otherPost.id !== id &&
              otherPost.workspaceId === post.workspaceId &&
              otherPost.channel === post.channel &&
              otherPost.day === day &&
              postStatusHoldsDailySlot(otherPost.status)
          )
        )
          return 'taken';
        const dayAndTimeLabel = (dayNumber: number, clockTime: string) =>
          `${demoDayLabel(dayNumber)} ${clockTime}`;
        const from = dayAndTimeLabel(post.day, post.time);
        const to = dayAndTimeLabel(day, time);
        updatePost(id, (postToUpdate) => ({
          day,
          time,
          needsNewTime: undefined,
          status: postToUpdate.status === 'Approved · Stopped' ? 'Scheduled' : postToUpdate.status,
          history: [
            ...postToUpdate.history,
            {
              at: nowLabel(),
              by: actor.name,
              text: 'Moved from {from} to {to}',
              variables: { from, to },
            },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} moved “{title}” ({channel}) from {from} to {to}.',
          { name: actor.name, title: post.title, channel: post.channel, from, to },
          id
        );
        return 'ok';
      },
      suggestTime(id: string, day: number, time: string, note: string, actor: Actor) {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post) return;
        const when = `${demoDayLabel(day)} ${time}`;
        updatePost(id, (postToUpdate) => ({
          timeRequest: { day, time, by: actor.name, note: note || undefined, at: nowLabel() },
          history: [
            ...postToUpdate.history,
            {
              at: nowLabel(),
              by: actor.name,
              text: 'Suggested a new time: {when}',
              variables: { when },
            },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} suggested moving “{title}” ({channel}) to {when}. Hakusana will confirm.',
          { name: actor.name, title: post.title, channel: post.channel, when },
          id
        );
      },
      resolveTimeRequest(
        id: string,
        apply: boolean,
        actor: Actor
      ): 'ok' | 'taken' | 'past' | 'locked' {
        const post = stateRef.current.posts.find((item) => item.id === id);
        if (!post?.timeRequest) return 'locked';
        const timeRequest = post.timeRequest;
        if (apply) {
          const moveResult = commands.movePost(id, timeRequest.day, timeRequest.time, actor);
          if (moveResult !== 'ok') return moveResult;
          updatePost(id, () => ({ timeRequest: undefined }));
          return 'ok';
        }
        updatePost(id, (postToUpdate) => ({
          timeRequest: undefined,
          history: [
            ...postToUpdate.history,
            { at: nowLabel(), by: actor.name, text: 'Declined the suggested time' },
          ],
        }));
        logToChat(
          post.workspaceId,
          '{name} kept “{title}” at its current time.',
          { name: actor.name, title: post.title },
          id
        );
        return 'ok';
      },
      approveMany(ids: string[], actor: Actor) {
        let approvedCount = 0;
        ids.forEach((id) => {
          if (commands.approve(id, actor)) approvedCount++;
        });
        return approvedCount;
      },
      checkPublishResult(postId: string, accountName: string, actor: Actor) {
        updatePost(postId, (postToUpdate) => ({
          results: postToUpdate.results?.map((result) =>
            result.account === accountName && result.uncertain
              ? { account: result.account, succeeded: true, at: postToUpdate.time }
              : result
          ),
          history: [
            ...postToUpdate.history,
            {
              at: nowLabel(),
              by: actor.name,
              text: 'Checked {account}: it was published, nothing was posted twice',
              variables: { account: accountName },
            },
          ],
        }));
        return 'published' as const;
      },
      retryPublish(postId: string, accountName: string, actor: Actor): 'ok' | 'not-connected' {
        const latestState = stateRef.current;
        const post = latestState.posts.find((item) => item.id === postId);
        const account = latestState.accounts.find(
          (candidateAccount) =>
            candidateAccount.workspaceId === post?.workspaceId &&
            candidateAccount.name === accountName.replace(/ \((company page|personal)\)$/, '')
        );
        if (!post || !account || account.health !== 'Connected') return 'not-connected';
        updatePost(postId, (postToUpdate) => ({
          results: postToUpdate.results?.map((result) =>
            result.account === accountName
              ? { account: result.account, succeeded: true, at: nowLabel() }
              : result
          ),
          history: [
            ...postToUpdate.history,
            {
              at: nowLabel(),
              by: actor.name,
              text: 'Published again to {account}',
              variables: { account: accountName },
            },
          ],
        }));
        logToChat(
          post.workspaceId,
          '“{title}” is now published to {account} too.',
          { title: post.title, account: accountName },
          postId
        );
        return 'ok';
      },
      reconnect(accountId: string) {
        setState((current) => {
          const account = current.accounts.find((item) => item.id === accountId);
          return {
            ...current,
            accounts: current.accounts.map((item) =>
              item.id === accountId
                ? {
                    ...item,
                    health: 'Connected' as const,
                    connectedAt: dayLabelNow(),
                    expires: undefined,
                  }
                : item
            ),
            notices: current.notices.map((notice) =>
              account && notice.workspaceId === account.workspaceId && notice.link === 'settings'
                ? { ...notice, read: true }
                : notice
            ),
          };
        });
      },
      invite(
        workspaceId: string,
        name: string,
        email: string,
        permissions: { canApprove: boolean; canStop: boolean },
        actor: Actor
      ): 'ok' | 'exists' {
        const latestState = stateRef.current;
        if (
          latestState.members.some(
            (member) =>
              member.workspaceId === workspaceId &&
              member.email.toLowerCase() === email.toLowerCase() &&
              member.status !== 'Expired'
          )
        )
          return 'exists';
        setState((current) => ({
          ...current,
          members: [
            ...current.members.filter(
              (member) =>
                !(
                  member.workspaceId === workspaceId &&
                  member.email.toLowerCase() === email.toLowerCase()
                )
            ),
            {
              id: `mb-${Date.now()}`,
              workspaceId: workspaceId as WorkspaceId,
              name,
              email,
              role: 'Contributor',
              status: 'Invited',
              invitedAt: nowLabel(),
              invitedBy: actor.name,
              ...permissions,
            },
          ],
        }));
        logToChat(workspaceId, '{name} invited {person} as a contributor.', {
          name: actor.name,
          person: name,
        });
        return 'ok';
      },
      resendInvite(id: string, actor: Actor) {
        setState((current) => ({
          ...current,
          members: current.members.map((member) =>
            member.id === id
              ? {
                  ...member,
                  status: 'Invited' as const,
                  invitedAt: nowLabel(),
                  invitedBy: actor.name,
                }
              : member
          ),
        }));
      },
      revokeInvite(id: string) {
        setState((current) => ({
          ...current,
          members: current.members.filter(
            (member) => !(member.id === id && member.status !== 'Active')
          ),
        }));
      },
      acceptInvite(id: string) {
        setState((current) => ({
          ...current,
          members: current.members.map((member) =>
            member.id === id ? { ...member, status: 'Active' as const } : member
          ),
        }));
      },
      removeMember(id: string, actor: Actor) {
        const member = stateRef.current.members.find(
          (candidateMember) => candidateMember.id === id
        );
        if (!member || member.role === 'Owner') return;
        setState((current) => ({
          ...current,
          members: current.members.filter((remainingMember) => remainingMember.id !== id),
        }));
        logToChat(member.workspaceId, '{name} removed {person} from the workspace.', {
          name: actor.name,
          person: member.name,
        });
      },
      setMemberPermission(
        id: string,
        key: 'canApprove' | 'canStop' | 'canEditBrand',
        value: boolean
      ) {
        setState((current) => ({
          ...current,
          members: current.members.map((member) =>
            member.id === id ? { ...member, [key]: value } : member
          ),
        }));
      },
      saveBusiness(workspaceId: string, patch: Partial<Business>, actor: Actor) {
        setState((current) => ({
          ...current,
          business: current.business.map((business) =>
            business.workspaceId === workspaceId
              ? { ...business, ...patch, updatedBy: actor.name, updatedAt: dayLabelNow() }
              : business
          ),
        }));
      },
      setWorkspacePrefs(workspaceId: string, patch: Partial<WorkspaceSettings>) {
        setState((current) => ({
          ...current,
          settings: current.settings.map((workspaceSettings) =>
            workspaceSettings.workspaceId === workspaceId
              ? { ...workspaceSettings, ...patch }
              : workspaceSettings
          ),
        }));
      },
      testAi(
        workspaceId: string,
        connectionAttempt: { provider: string; model: string; key: string; scenario: AiScenario },
        by: string
      ): AiScenario {
        const at = `${dayLabelNow()}, ${nowLabel()}`;
        const attemptSucceeded = connectionAttempt.scenario === 'success';
        setState((current) => ({
          ...current,
          aiConnections: current.aiConnections.map((aiConnection) => {
            if (aiConnection.workspaceId !== workspaceId) return aiConnection;
            const failedAttemptLeavesWorkingConnectionUntouched =
              aiConnection.status === 'connected';
            if (!attemptSucceeded)
              return failedAttemptLeavesWorkingConnectionUntouched
                ? aiConnection
                : {
                    workspaceId: aiConnection.workspaceId,
                    status: 'error',
                    provider: connectionAttempt.provider,
                    model: connectionAttempt.model,
                    error: connectionAttempt.scenario,
                    history: aiConnection.history,
                  };
            let history = aiConnection.history ?? [];
            if (
              aiConnection.status === 'connected' &&
              aiConnection.model &&
              aiConnection.provider &&
              (aiConnection.model !== connectionAttempt.model ||
                aiConnection.provider !== connectionAttempt.provider)
            ) {
              const keysCountedForEarlierModels = new Set(
                history.flatMap((historyEntry) => historyEntry.keys)
              );
              const usageKeysOfReplacedModel = usageRows(
                current.posts.filter((post) => post.workspaceId === workspaceId),
                DEMO_TODAY
              )
                .map((usageRow) => usageRow.key)
                .filter((usageKey) => !keysCountedForEarlierModels.has(usageKey));
              history = [
                ...history,
                {
                  provider: aiConnection.provider,
                  model: aiConnection.model,
                  keys: usageKeysOfReplacedModel,
                },
              ];
            }
            return {
              workspaceId: aiConnection.workspaceId,
              status: 'connected',
              provider: connectionAttempt.provider,
              model: connectionAttempt.model,
              keyEnding: connectionAttempt.key.slice(-4),
              lastTest: at,
              connectedBy: by,
              history,
            };
          }),
        }));
        if (attemptSucceeded)
          logToChat(workspaceId, '{name} connected AI ({provider}). AMY can draft with it now.', {
            name: by,
            provider: connectionAttempt.provider,
          });
        return connectionAttempt.scenario;
      },
      retestAi(workspaceId: string) {
        setState((current) => ({
          ...current,
          aiConnections: current.aiConnections.map((aiConnection) =>
            aiConnection.workspaceId === workspaceId && aiConnection.status === 'connected'
              ? { ...aiConnection, lastTest: `${dayLabelNow()}, ${nowLabel()}`, error: undefined }
              : aiConnection
          ),
        }));
      },
      removeAi(workspaceId: string, actor: Actor) {
        setState((current) => ({
          ...current,
          aiConnections: current.aiConnections.map((aiConnection) =>
            aiConnection.workspaceId === workspaceId
              ? {
                  workspaceId: aiConnection.workspaceId,
                  status: 'none',
                  trial: false,
                  history: aiConnection.history,
                }
              : aiConnection
          ),
        }));
        logToChat(
          workspaceId,
          '{name} removed the AI key. AMY stops drafting new posts until a key is connected.',
          { name: actor.name }
        );
      },
      inviteHelper(workspaceId: string, name: string, email: string, actor: Actor) {
        setState((current) => ({
          ...current,
          helpers: [
            ...current.helpers.filter(
              (helper) => !(helper.workspaceId === workspaceId && helper.status === 'Pending')
            ),
            {
              id: `hl-${Date.now()}`,
              workspaceId: workspaceId as WorkspaceId,
              name,
              email,
              scope: 'ai',
              status: 'Pending',
              sentAt: `${dayLabelNow()}, ${nowLabel()}`,
              by: actor.name,
            },
          ],
        }));
      },
      resendHelper(id: string) {
        setState((current) => ({
          ...current,
          helpers: current.helpers.map((helper) =>
            helper.id === id ? { ...helper, sentAt: `${dayLabelNow()}, ${nowLabel()}` } : helper
          ),
        }));
      },
      withdrawHelper(id: string) {
        setState((current) => ({
          ...current,
          helpers: current.helpers.map((helper) =>
            helper.id === id ? { ...helper, status: 'Withdrawn' } : helper
          ),
        }));
      },
      completeHelper(id: string) {
        setState((current) => ({
          ...current,
          helpers: current.helpers.map((helper) =>
            helper.id === id
              ? { ...helper, status: 'Completed', completedAt: `${dayLabelNow()}, ${nowLabel()}` }
              : helper
          ),
        }));
      },
      setSetupLater(workspaceId: string, setupStep: SetupStep, markAsLater: boolean) {
        setState((current) => ({
          ...current,
          onboarding: current.onboarding.map((onboarding) =>
            onboarding.workspaceId !== workspaceId
              ? onboarding
              : {
                  ...onboarding,
                  later: markAsLater
                    ? [...new Set([...onboarding.later, setupStep])]
                    : onboarding.later.filter((laterStep) => laterStep !== setupStep),
                }
          ),
        }));
      },
      activateSetup(workspaceId: string, actor: Actor) {
        setState((current) => ({
          ...current,
          onboarding: current.onboarding.map((onboarding) =>
            onboarding.workspaceId === workspaceId
              ? {
                  ...onboarding,
                  activated: dayLabelNow(),
                  later: onboarding.later.filter((laterStep) => laterStep !== 'activate'),
                }
              : onboarding
          ),
        }));
        logToChat(
          workspaceId,
          '{name} activated the posting plan. AMY drafts the first batch for review.',
          { name: actor.name }
        );
      },
      connectAccount(
        workspaceId: string,
        chosenAccount: { network: Channel; name: string; type: string },
        actor: Actor,
        replaceId?: string
      ): { id: string; reapproval: number } {
        const latestState = stateRef.current;
        const matchingExistingAccount = latestState.accounts.find(
          (existingAccount) =>
            existingAccount.workspaceId === workspaceId &&
            existingAccount.network === chosenAccount.network &&
            existingAccount.type === chosenAccount.type &&
            (replaceId
              ? existingAccount.id === replaceId
              : existingAccount.name === chosenAccount.name)
        );
        const isReplacedByDifferentlyNamedAccount =
          replaceId &&
          matchingExistingAccount &&
          matchingExistingAccount.name !== chosenAccount.name;
        const id =
          matchingExistingAccount?.id ??
          `${workspaceId}-${chosenAccount.network.toLowerCase()}-${Date.now()}`;
        setState((current) => ({
          ...current,
          accounts: matchingExistingAccount
            ? current.accounts.map((account) =>
                account.id === matchingExistingAccount.id
                  ? {
                      ...account,
                      name: chosenAccount.name,
                      health: 'Connected' as const,
                      connectedAt: dayLabelNow(),
                      expires: undefined,
                    }
                  : account
              )
            : [
                ...current.accounts,
                {
                  id,
                  workspaceId: workspaceId as WorkspaceId,
                  network: chosenAccount.network,
                  name: chosenAccount.name,
                  type: chosenAccount.type,
                  health: 'Connected' as const,
                  owner: actor.name,
                  connectedAt: dayLabelNow(),
                },
              ],
          posts: isReplacedByDifferentlyNamedAccount
            ? current.posts.map((post) =>
                post.workspaceId !== workspaceId ||
                post.channel !== chosenAccount.network ||
                post.account !== matchingExistingAccount.name ||
                post.status === 'Published' ||
                post.status === 'Cancelled'
                  ? post
                  : {
                      ...post,
                      account: chosenAccount.name,
                      ...(STATUSES_THAT_NEED_APPROVAL_AGAIN_ON_NEW_ACCOUNT.includes(post.status)
                        ? { status: 'Needs your review' as PostStatus, needsNewTime: undefined }
                        : {}),
                      history: [
                        ...post.history,
                        {
                          at: nowLabel(),
                          by: actor.name,
                          text: 'Account changed from {from} to {to}; needs approval again',
                          variables: { from: matchingExistingAccount.name, to: chosenAccount.name },
                        },
                      ],
                    }
              )
            : current.posts,
          notices: current.notices.map((notice) =>
            notice.workspaceId === workspaceId && notice.link === 'settings'
              ? { ...notice, read: true }
              : notice
          ),
        }));
        const reapproval = isReplacedByDifferentlyNamedAccount
          ? latestState.posts.filter(
              (post) =>
                post.workspaceId === workspaceId &&
                post.channel === chosenAccount.network &&
                post.account === matchingExistingAccount.name &&
                STATUSES_THAT_NEED_APPROVAL_AGAIN_ON_NEW_ACCOUNT.includes(post.status)
            ).length
          : 0;
        return { id, reapproval };
      },
      setNotify(
        workspaceId: string,
        event: NoticeEvent,
        channel: NoticeChannel,
        on: boolean,
        person: string
      ) {
        setState((current) => ({
          ...current,
          settings: current.settings.map((workspaceSettings) => {
            if (workspaceSettings.workspaceId !== workspaceId) return workspaceSettings;
            const personalPreferences = preferencesFor(workspaceSettings, person);
            const currentChannels = personalPreferences.notify[event] ?? [];
            const updatedChannels = on
              ? [...new Set([...currentChannels, channel])]
              : currentChannels.filter((existingChannel) => existingChannel !== channel);
            return {
              ...workspaceSettings,
              personal: {
                ...workspaceSettings.personal,
                [person]: {
                  ...personalPreferences,
                  notify: { ...personalPreferences.notify, [event]: updatedChannels },
                },
              },
            };
          }),
        }));
      },
      setPersonalPrefs(workspaceId: string, person: string, patch: Partial<PersonalPreferences>) {
        setState((current) => ({
          ...current,
          settings: current.settings.map((workspaceSettings) =>
            workspaceSettings.workspaceId === workspaceId
              ? {
                  ...workspaceSettings,
                  personal: {
                    ...workspaceSettings.personal,
                    [person]: { ...preferencesFor(workspaceSettings, person), ...patch },
                  },
                }
              : workspaceSettings
          ),
        }));
      },
      disconnectAccount(accountId: string, actor: Actor) {
        const account = stateRef.current.accounts.find(
          (candidateAccount) => candidateAccount.id === accountId
        );
        if (!account) return 0;
        const waiting = stateRef.current.posts.filter(
          (post) =>
            post.workspaceId === account.workspaceId &&
            onAccount(post, account) &&
            post.status === 'Scheduled' &&
            post.day >= DEMO_TODAY
        ).length;
        setState((current) => ({
          ...current,
          accounts: current.accounts.map((existingAccount) =>
            existingAccount.id === accountId
              ? { ...existingAccount, health: 'Not connected' as const }
              : existingAccount
          ),
        }));
        logToChat(
          account.workspaceId,
          '{name} disconnected {account} ({network}). {n} scheduled posts wait until it is connected again.',
          { name: actor.name, account: account.name, network: account.network, n: String(waiting) }
        );
        return waiting;
      },
      assignStaff(staffId: string, workspaceId: string, on: boolean) {
        setState((current) => ({
          ...current,
          staff: current.staff.map((staffMember) =>
            staffMember.id === staffId
              ? {
                  ...staffMember,
                  workspaces: on
                    ? [...new Set([...staffMember.workspaces, workspaceId as WorkspaceId])]
                    : staffMember.workspaces.filter(
                        (assignedWorkspaceId) => assignedWorkspaceId !== workspaceId
                      ),
                  lead: on
                    ? staffMember.lead
                    : (staffMember.lead ?? []).filter(
                        (ledWorkspaceId) => ledWorkspaceId !== workspaceId
                      ),
                }
              : staffMember
          ),
        }));
      },
      setLead(staffId: string, workspaceId: string) {
        setState((current) => ({
          ...current,
          staff: current.staff.map((staffMember) => ({
            ...staffMember,
            lead:
              staffMember.id === staffId
                ? [...new Set([...(staffMember.lead ?? []), workspaceId as WorkspaceId])]
                : (staffMember.lead ?? []).filter(
                    (ledWorkspaceId) => ledWorkspaceId !== workspaceId
                  ),
            workspaces:
              staffMember.id === staffId
                ? [...new Set([...staffMember.workspaces, workspaceId as WorkspaceId])]
                : staffMember.workspaces,
          })),
        }));
      },
      addAccount(account: Account) {
        setState((current) => ({ ...current, accounts: [...current.accounts, account] }));
      },
      saveBrandDraft(
        workspaceId: string,
        change: (draft: BrandSettings) => BrandSettings,
        actor?: Actor
      ) {
        setState((current) => ({
          ...current,
          brands: current.brands.map((brand) =>
            brand.workspaceId === workspaceId
              ? {
                  ...brand,
                  draft: change(clone(brand.draft ?? brand.active)),
                  draftBy: brand.draft ? brand.draftBy : actor?.name,
                }
              : brand
          ),
        }));
      },
      restoreBrandVersion(workspaceId: string, version: number, actor: Actor) {
        setState((current) => ({
          ...current,
          brands: current.brands.map((brand) => {
            if (brand.workspaceId !== workspaceId) return brand;
            const snapshot = brand.history.find((item) => item.version === version)?.snapshot;
            return snapshot ? { ...brand, draft: clone(snapshot), draftBy: actor.name } : brand;
          }),
        }));
      },
      discardBrandDraft(workspaceId: string) {
        setState((current) => ({
          ...current,
          brands: current.brands.map((brand) =>
            brand.workspaceId === workspaceId
              ? { ...brand, draft: null, draftBy: undefined }
              : brand
          ),
        }));
      },
      applyBrand(
        workspaceId: string,
        parts: { key: string; variables?: Record<string, string> }[],
        actor: Actor
      ) {
        setState((current) => ({
          ...current,
          brands: current.brands.map((brand) =>
            brand.workspaceId === workspaceId && brand.draft
              ? {
                  ...brand,
                  version: brand.version + 1,
                  active: brand.draft,
                  draft: null,
                  draftBy: undefined,
                  history: [
                    ...brand.history,
                    {
                      version: brand.version + 1,
                      by: actor.name,
                      day: 18,
                      summary: parts.map((part) => part.key).join(', '),
                      parts,
                      snapshot: brand.draft,
                    },
                  ],
                }
              : brand
          ),
        }));
        logToChat(
          workspaceId,
          '{name} applied brand guide v{v}. AMY uses it for new content from now on.',
          {
            name: actor.name,
            v: String(
              (stateRef.current.brands.find((brand) => brand.workspaceId === workspaceId)
                ?.version ?? 0) + 1
            ),
          }
        );
      },
      sendMessage(
        conversationId: string,
        text: string,
        actor: Actor,
        attachments?: { name: string; src?: string }[]
      ) {
        const targetConversation = stateRef.current.conversations.find(
          (item) => item.id === conversationId
        );
        if (!targetConversation) return;
        const campaign = stateRef.current.campaigns.find(
          (candidateCampaign) => candidateCampaign.conversationId === conversationId
        );
        const userMessage: Message = {
          id: nextMessageId(),
          from: 'user',
          by: actor.name,
          at: nowLabel(),
          text,
          showAsWritten: true,
          attachments,
        };
        const guessedKind = guessKind(text);
        const kind = guessedKind === 'ideas' && !campaign ? 'post' : guessedKind;
        if (kind === 'note' && !attachments?.length) {
          const reply: Message = {
            id: nextMessageId(),
            from: 'amy',
            at: nowLabel(),
            text: /\b(thanks?|thank you|dzięki|dziękuję)\b/i.test(text)
              ? 'You are welcome! Tell me whenever you want me to prepare something.'
              : 'Noted. If you want me to make a post or ideas from this, just say so.',
          };
          setState((current) => ({
            ...current,
            conversations: current.conversations.map((item) =>
              item.id === conversationId
                ? { ...item, messages: [...item.messages, userMessage, reply] }
                : item
            ),
          }));
          return;
        }
        const taskId = `t-${Date.now()}`;
        const now = Date.now();
        const channel = guessChannel(text, campaign);
        const day = dayFrom(text);
        const materials = attachments?.map((item) => item.name);
        const task: Task = {
          id: taskId,
          workspaceId: targetConversation.workspaceId,
          title: text,
          showAsWritten: true,
          status: 'Queued',
          kind: kind === 'note' ? 'post' : kind,
          by: actor.name,
          at: nowLabel(),
          conversationId,
          campaignId: campaign?.id,
          startAt: now + 1200,
          readyAt: now + 6500,
          day,
          channels: [channel],
          materials,
          interpretation:
            kind === 'ideas'
              ? { text: 'New ideas for “{name}”.', variables: { name: campaign?.name ?? '' } }
              : {
                  text: 'A {channel} post about “{title}”{when}.',
                  variables: { channel, title: titleFrom(text), when: day ? ` · ${day} Nov` : '' },
                },
          nextStep:
            kind === 'ideas'
              ? 'AMY suggests ideas; you choose which go into the calendar.'
              : 'AMY drafts it in the first free slot, then asks you to review.',
        };
        const reply: Message = {
          id: nextMessageId(),
          from: 'amy',
          at: nowLabel(),
          text: aiWorks(
            stateRef.current.aiConnections.find(
              (aiConnection) =>
                aiConnection.workspaceId ===
                stateRef.current.conversations.find(
                  (conversation) => conversation.id === conversationId
                )?.workspaceId
            )
          )
            ? 'Got it. I have added this as a task and will tell you here when a draft is ready for review.'
            : 'Got it. I have saved this as a task. AI is not connected right now, so I will start as soon as it is.',
          task: { title: text, showAsWritten: true, status: 'Queued' },
          taskId,
        };
        setState((current) => ({
          ...current,
          tasks: [task, ...current.tasks],
          conversations: current.conversations.map((item) =>
            item.id === conversationId
              ? { ...item, messages: [...item.messages, userMessage, reply] }
              : item
          ),
        }));
      },
      addIdea(
        workspaceId: string,
        idea: {
          text: string;
          materials: string[];
          day?: number;
          campaignId?: string;
          channels: Channel[];
          allowAfter?: boolean;
        },
        actor: Actor
      ) {
        const latestState = stateRef.current;
        const generalConversation = latestState.conversations.find(
          (conversation) =>
            conversation.workspaceId === workspaceId && conversation.scope === 'general'
        );
        const campaign = latestState.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === idea.campaignId
        );
        const now = Date.now();
        const id = `t-idea-${now}`;
        const title = titleFrom(idea.text);
        const task: Task = {
          id,
          workspaceId: workspaceId as WorkspaceId,
          title: idea.text,
          showAsWritten: true,
          status: 'Queued',
          kind: 'post',
          by: actor.name,
          at: nowLabel(),
          conversationId: generalConversation?.id,
          campaignId: idea.campaignId,
          startAt: now + 900,
          readyAt: now + 6000,
          day: idea.day,
          channels: idea.channels,
          materials: idea.materials,
          allowAfter: idea.allowAfter,
          interpretation: {
            text: '{channels} post about “{title}”{when}{campaign}.',
            variables: {
              channels: idea.channels.join(' + '),
              title,
              when: idea.day ? ` · ${demoDayLabel(idea.day)}` : '',
              campaign: campaign ? ` · ${campaign.name}` : '',
            },
          },
          nextStep: 'AMY drafts one version per channel, then asks you to review.',
        };
        const ideaMessages: Message[] = [
          {
            id: nextMessageId(),
            from: 'user',
            by: actor.name,
            at: nowLabel(),
            text: idea.text,
            showAsWritten: true,
            attachments: idea.materials.map((name) => ({
              name,
              src: latestState.assets.find((asset) => asset.name === name)?.image,
            })),
          },
          {
            id: nextMessageId(),
            from: 'amy',
            at: nowLabel(),
            text: aiWorks(
              latestState.aiConnections.find(
                (aiConnection) => aiConnection.workspaceId === workspaceId
              )
            )
              ? 'Thanks for the idea! I am turning it into a draft.'
              : 'Thanks for the idea! It is saved. AI is not connected right now, so I will draft it as soon as it is.',
            task: { title: idea.text, showAsWritten: true, status: 'Queued' },
            taskId: id,
          },
        ];
        setState((current) => ({
          ...current,
          tasks: [task, ...current.tasks],
          conversations: current.conversations.map((conversation) =>
            conversation.id === generalConversation?.id
              ? { ...conversation, messages: [...conversation.messages, ...ideaMessages] }
              : conversation
          ),
        }));
        return id;
      },
      startTask(id: string) {
        const now = Date.now();
        setState((current) => ({
          ...current,
          tasks: current.tasks.map((task) =>
            task.id === id &&
            (task.status === 'Queued' || task.status === 'Failed' || task.status === 'With agency')
              ? {
                  ...task,
                  status: 'Queued' as const,
                  plannedFor: undefined,
                  reason: undefined,
                  startAt: now + 600,
                  readyAt: now + 5000,
                }
              : task
          ),
        }));
      },
      cancelTask(id: string) {
        setState((current) => ({
          ...current,
          tasks: current.tasks.map((task) =>
            task.id === id && ['Queued', 'Failed', 'Waiting', 'With agency'].includes(task.status)
              ? { ...task, status: 'Cancelled' as const }
              : task
          ),
        }));
      },
      answerTask(id: string, answer: string, actor: Actor) {
        const task = stateRef.current.tasks.find((candidateTask) => candidateTask.id === id);
        if (!task) return;
        const now = Date.now();
        if (task.kind === 'post') {
          setState((current) => ({
            ...current,
            tasks: current.tasks.map((existingTask) =>
              existingTask.id === id
                ? {
                    ...existingTask,
                    status: 'Queued' as const,
                    question: undefined,
                    questionVars: undefined,
                    choices: undefined,
                    allowAfter: answer === 'allowAfter',
                    skipLate: answer === 'skipLate',
                    startAt: now + 500,
                    readyAt: now + 4000,
                    nextStep: 'AMY drafts it with your choice.',
                  }
                : existingTask
            ),
          }));
          logToChat(
            task.workspaceId,
            answer === 'allowAfter'
              ? '{name} chose the next free days for “{title}”.'
              : '{name} chose only the channels that fit for “{title}”.',
            { name: actor.name, title: titleFrom(task.title) }
          );
          return;
        }
        setState((current) => ({
          ...current,
          tasks: current.tasks.map((existingTask) =>
            existingTask.id === id
              ? {
                  ...existingTask,
                  status: 'Queued' as const,
                  answer,
                  question: undefined,
                  startAt: now + 600,
                  readyAt: now + 5000,
                  nextStep: 'AMY writes the plan with your answer.',
                }
              : existingTask
          ),
          campaigns: current.campaigns.map((campaign) =>
            campaign.id === task.campaignId
              ? {
                  ...campaign,
                  question: undefined,
                  status:
                    campaign.status === 'Needs your input'
                      ? ('Planning' as const)
                      : campaign.status,
                  brief: `${campaign.brief}\n${answer}`,
                }
              : campaign
          ),
        }));
        logToChat(task.workspaceId, '{name} answered AMY: {text}', {
          name: actor.name,
          text: answer,
        });
      },
      setSlotOverride(workspaceId: string, channel: Channel, value: number | null) {
        setState((current) => {
          const overridesForWorkspace = { ...(current.dailyLimitOverrides[workspaceId] ?? {}) };
          if (value === null) delete overridesForWorkspace[channel];
          else overridesForWorkspace[channel] = value;
          return {
            ...current,
            dailyLimitOverrides: {
              ...current.dailyLimitOverrides,
              [workspaceId]: overridesForWorkspace,
            },
          };
        });
      },
      contactAgency(workspaceId: string, text: string, actor: Actor) {
        setState((current) => ({
          ...current,
          requests: [
            ...current.requests,
            {
              id: `rq-${Date.now()}`,
              workspaceId: workspaceId as WorkspaceId,
              from: actor.name,
              text,
              at: nowLabel(),
              status: 'Open',
            },
          ],
        }));
        logToChat(workspaceId, '{name} wrote to Hakusana: {text}', { name: actor.name, text });
      },
      replyToRequest(id: string, text: string, actor: Actor) {
        const request = stateRef.current.requests.find(
          (candidateRequest) => candidateRequest.id === id
        );
        if (!request) return;
        setState((current) => ({
          ...current,
          requests: current.requests.map((existingRequest) =>
            existingRequest.id === id
              ? {
                  ...existingRequest,
                  status: 'Answered' as const,
                  reply: { by: actor.name, text, at: nowLabel() },
                }
              : existingRequest
          ),
          notices: [
            ...current.notices,
            {
              id: `n-rq-${Date.now()}`,
              workspaceId: request.workspaceId,
              at: nowLabel(),
              text: '{name} from Hakusana replied to your message',
              variables: { name: actor.name },
              link: 'calendar',
              read: false,
            },
          ],
        }));
        logToChat(request.workspaceId, '{name} (Hakusana) replied: {text}', {
          name: actor.name,
          text,
        });
      },
      completeHandedTask(id: string, note: string, actor: Actor) {
        const task = stateRef.current.tasks.find((candidateTask) => candidateTask.id === id);
        if (!task) return;
        setState((current) => ({
          ...current,
          tasks: current.tasks.map((existingTask) =>
            existingTask.id === id
              ? {
                  ...existingTask,
                  status: 'Ready' as const,
                  question: undefined,
                  choices: undefined,
                  nextStep: undefined,
                  result: {
                    text: '{name} (Hakusana) finished this: {note}',
                    variables: { name: actor.name, note },
                  },
                }
              : existingTask
          ),
        }));
        logToChat(task.workspaceId, '{name} (Hakusana) finished “{title}”: {note}', {
          name: actor.name,
          title: task.showAsWritten ? task.title : titleFrom(task.title),
          note,
        });
      },
      handToAgency(id: string, actor: Actor) {
        const task = stateRef.current.tasks.find((candidateTask) => candidateTask.id === id);
        if (!task) return;
        setState((current) => ({
          ...current,
          tasks: current.tasks.map((existingTask) =>
            existingTask.id === id
              ? {
                  ...existingTask,
                  status: 'With agency' as const,
                  nextStep: 'Hakusana will pick this up and reply in this conversation.',
                }
              : existingTask
          ),
        }));
        logToChat(task.workspaceId, '{name} handed a task to Hakusana: {text}', {
          name: actor.name,
          text: task.showAsWritten ? task.title : task.title,
        });
      },
      saveCampaign(
        workspaceId: string,
        draft: Pick<
          Campaign,
          'name' | 'goal' | 'start' | 'end' | 'channels' | 'perWeek' | 'brief' | 'materials'
        > &
          Partial<
            Pick<Campaign, 'audience' | 'focus' | 'languages' | 'accounts' | 'template' | 'plan'>
          >,
        actor: Actor,
        options: { id?: string; askIdeas?: boolean }
      ) {
        const now = Date.now();
        const id = options.id ?? `cmp-${now}`;
        const existingCampaign = stateRef.current.campaigns.find((campaign) => campaign.id === id);
        const conversationId = existingCampaign?.conversationId ?? `conv-${now}`;
        setState((current) => {
          let campaigns = current.campaigns;
          let conversations = current.conversations;
          if (existingCampaign) {
            campaigns = campaigns.map((campaign) =>
              campaign.id === id ? { ...campaign, ...draft } : campaign
            );
            conversations = conversations.map((conversation) =>
              conversation.id === conversationId && conversation.showAsWritten
                ? { ...conversation, title: draft.name }
                : conversation
            );
          } else {
            const status = draft.plan?.length
              ? ('Plan ready' as const)
              : options.askIdeas
                ? ('Planning' as const)
                : ('Draft' as const);
            campaigns = [
              ...campaigns,
              {
                ...draft,
                id,
                workspaceId: workspaceId as WorkspaceId,
                key: draft.name,
                status,
                ideas: [],
                conversationId: conversationId,
                createdBy: actor.name,
                owner: actor.name,
                batchSize: 3,
              },
            ];
            conversations = [
              ...conversations,
              {
                id: conversationId,
                workspaceId: workspaceId as WorkspaceId,
                title: draft.name,
                showAsWritten: true,
                scope: 'campaign',
                unread: 0,
                messages: [
                  {
                    id: nextMessageId(),
                    from: 'system',
                    at: nowLabel(),
                    text: '{name} created the campaign “{title}”.',
                    variables: { name: actor.name, title: draft.name },
                  },
                ],
              },
            ];
          }
          const tasks = options.askIdeas
            ? [
                {
                  id: `t-${now}`,
                  workspaceId: workspaceId as WorkspaceId,
                  title: 'Suggest ideas for “{name}”',
                  variables: { name: draft.name },
                  status: 'Queued' as const,
                  kind: 'ideas' as const,
                  by: actor.name,
                  at: nowLabel(),
                  conversationId: conversationId,
                  campaignId: id,
                  startAt: now + 800,
                  readyAt: now + 5500,
                  interpretation: {
                    text: 'New ideas for “{name}”.',
                    variables: { name: draft.name },
                  },
                  nextStep: 'AMY suggests ideas; you choose which go into the calendar.',
                },
                ...current.tasks,
              ]
            : current.tasks;
          return {
            ...current,
            campaigns:
              options.askIdeas && existingCampaign
                ? campaigns.map((campaign) =>
                    campaign.id === id && campaign.status === 'Draft'
                      ? { ...campaign, status: 'Planning' as const }
                      : campaign
                  )
                : campaigns,
            conversations,
            tasks: tasks as Task[],
          };
        });
        return id;
      },
      requestIdeas(campaignId: string, actor: Actor) {
        const campaign = stateRef.current.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        if (!campaign) return;
        const now = Date.now();
        setState((current) => ({
          ...current,
          campaigns: current.campaigns.map((existingCampaign) =>
            existingCampaign.id === campaignId && existingCampaign.status === 'Draft'
              ? { ...existingCampaign, status: 'Planning' as const }
              : existingCampaign
          ),
          tasks: [
            {
              id: `t-${now}`,
              workspaceId: campaign.workspaceId,
              title: 'Suggest ideas for “{name}”',
              variables: { name: campaign.name },
              status: 'Queued',
              kind: 'ideas',
              by: actor.name,
              at: nowLabel(),
              conversationId: campaign.conversationId,
              campaignId,
              startAt: now + 600,
              readyAt: now + 5000,
              interpretation: {
                text: 'New ideas for “{name}”.',
                variables: { name: campaign.name },
              },
              nextStep: 'AMY suggests ideas; you choose which go into the calendar.',
            },
            ...current.tasks,
          ],
        }));
      },
      answerCampaignQuestion(campaignId: string, answer: string, actor: Actor) {
        const latestState = stateRef.current;
        const campaign = latestState.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        if (!campaign) return;
        const waitingOrHandedOverTask = latestState.tasks.find(
          (task) =>
            task.campaignId === campaignId &&
            (task.status === 'Waiting' || task.status === 'With agency')
        );
        if (waitingOrHandedOverTask) {
          commands.answerTask(waitingOrHandedOverTask.id, answer, actor);
          return;
        }
        const now = Date.now();
        setState((current) => ({
          ...current,
          campaigns: current.campaigns.map((existingCampaign) =>
            existingCampaign.id === campaignId
              ? {
                  ...existingCampaign,
                  question: undefined,
                  status: 'Planning' as const,
                  brief: `${existingCampaign.brief}\n${answer}`,
                }
              : existingCampaign
          ),
          tasks: [
            {
              id: `t-${now}`,
              workspaceId: campaign.workspaceId,
              title: 'Plan “{name}”',
              variables: { name: campaign.name },
              status: 'Queued',
              kind: 'plan',
              by: actor.name,
              at: nowLabel(),
              conversationId: campaign.conversationId,
              campaignId,
              startAt: now + 600,
              readyAt: now + 5000,
            },
            ...current.tasks,
          ],
        }));
        logToChat(campaign.workspaceId, '{name} answered AMY’s question for “{title}”.', {
          name: actor.name,
          title: campaign.name,
        });
      },
      acceptIdea(
        campaignId: string,
        ideaId: string,
        actor: Actor
      ):
        | { ok: true; day: number; postId: string }
        | { ok: false; reason: 'slot' | 'paused' | 'account' | 'channel'; channel: Channel } {
        const latestState = stateRef.current;
        const campaign = latestState.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        const idea = campaign?.ideas.find((candidateIdea) => candidateIdea.id === ideaId);
        if (!campaign || !idea) return { ok: false, reason: 'slot', channel: 'Instagram' };
        if (campaign.creationPaused || campaign.status === 'Finished')
          return { ok: false, reason: 'paused', channel: idea.channel };
        if (!campaign.channels.includes(idea.channel))
          return { ok: false, reason: 'channel', channel: idea.channel };
        const account = latestState.accounts.find(
          (candidateAccount) =>
            candidateAccount.workspaceId === campaign.workspaceId &&
            candidateAccount.network === idea.channel &&
            !/personal/i.test(candidateAccount.type)
        );
        if (!account || account.health !== 'Connected')
          return { ok: false, reason: 'account', channel: idea.channel };
        const day = freeDay(
          latestState.posts,
          campaign.workspaceId,
          idea.channel,
          campaign.start ?? 19,
          campaign.end ?? 30
        );
        if (!day) return { ok: false, reason: 'slot', channel: idea.channel };
        const post = draftPost({
          workspaceId: campaign.workspaceId,
          title: idea.title,
          channel: idea.channel,
          format: idea.format,
          day,
          campaign,
          accounts: latestState.accounts,
          assets: latestState.assets,
          brief: idea.angle,
          status: 'AMY is working',
        });
        setState((current) => ({
          ...current,
          posts: [
            ...current.posts,
            { ...post, history: [{ at: nowLabel(), by: actor.name, text: 'Accepted the idea' }] },
          ],
          campaigns: current.campaigns.map((existingCampaign) =>
            existingCampaign.id === campaignId
              ? {
                  ...existingCampaign,
                  status:
                    existingCampaign.status === 'Ideas ready' ||
                    existingCampaign.status === 'Planning'
                      ? ('Active' as const)
                      : existingCampaign.status,
                  ideas: existingCampaign.ideas.map((existingIdea) =>
                    existingIdea.id === ideaId
                      ? { ...existingIdea, status: 'In calendar' as const, postId: post.id }
                      : existingIdea
                  ),
                }
              : existingCampaign
          ),
          revising: [
            ...current.revising,
            { postId: post.id, readyAt: Date.now() + 5000, note: FIRST_DRAFT },
          ],
        }));
        return { ok: true, day, postId: post.id };
      },
      setIdeaStatus(campaignId: string, ideaId: string, status: 'Suggested' | 'Declined') {
        setState((current) => ({
          ...current,
          campaigns: current.campaigns.map((campaign) =>
            campaign.id === campaignId
              ? {
                  ...campaign,
                  ideas: campaign.ideas.map((idea) =>
                    idea.id === ideaId && idea.status !== 'In calendar' ? { ...idea, status } : idea
                  ),
                }
              : campaign
          ),
        }));
      },
      setCampaignPause(
        campaignId: string,
        pauseTarget: 'creation' | 'publishing',
        paused: boolean,
        actor: Actor
      ) {
        const campaign = stateRef.current.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        if (!campaign) return { held: 0, blocked: 0 };
        let held = 0;
        let blocked = 0;
        const at = nowLabel();
        setState((current) => {
          let posts = current.posts;
          if (pauseTarget === 'publishing') {
            posts = current.posts.map((post) => {
              if (
                post.workspaceId !== campaign.workspaceId ||
                post.campaign !== campaign.key ||
                post.day <= DEMO_TODAY
              )
                return post;
              if (paused && post.status === 'Scheduled') {
                held++;
                return {
                  ...post,
                  status: 'Approved · Stopped' as const,
                  history: [
                    ...post.history,
                    { at, by: actor.name, text: 'Held: campaign publishing paused' },
                  ],
                };
              }
              if (
                !paused &&
                post.status === 'Approved · Stopped' &&
                post.history[post.history.length - 1]?.text === 'Held: campaign publishing paused'
              ) {
                const taken = current.posts.some(
                  (otherPost) =>
                    otherPost.id !== post.id &&
                    otherPost.workspaceId === post.workspaceId &&
                    otherPost.channel === post.channel &&
                    otherPost.day === post.day &&
                    postStatusHoldsDailySlot(otherPost.status)
                );
                if (taken) {
                  blocked++;
                  return post;
                }
                held++;
                return {
                  ...post,
                  status: 'Scheduled' as const,
                  history: [...post.history, { at, by: actor.name, text: 'Publishing resumed' }],
                };
              }
              return post;
            });
          }
          return {
            ...current,
            posts,
            campaigns: current.campaigns.map((existingCampaign) =>
              existingCampaign.id === campaignId
                ? {
                    ...existingCampaign,
                    [pauseTarget === 'creation' ? 'creationPaused' : 'publishingPaused']: paused,
                  }
                : existingCampaign
            ),
          };
        });
        const text =
          pauseTarget === 'creation'
            ? paused
              ? '{name} paused content creation for “{title}”. Scheduled posts still publish.'
              : '{name} resumed content creation for “{title}”.'
            : paused
              ? '{name} paused publishing for “{title}”. Scheduled posts are held.'
              : '{name} resumed publishing for “{title}”.';
        logToChat(campaign.workspaceId, text, { name: actor.name, title: campaign.name });
        return { held, blocked };
      },
      activateCampaign(campaignId: string, actor: Actor) {
        const latestState = stateRef.current;
        const campaign = latestState.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        if (!campaign?.plan?.length) return 0;
        return draftSlots(
          campaign,
          campaign.plan.filter((slot) => !slot.postId).slice(0, 2),
          actor,
          { samples: true }
        );
      },
      runBatch(campaignId: string, actor: Actor | 'AMY') {
        const campaign = stateRef.current.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        if (!campaign || campaign.creationPaused) return 0;
        let slots =
          campaign.plan?.filter((slot) => !slot.postId).slice(0, campaign.batchSize ?? 3) ?? [];
        if (!campaign.plan) {
          const ideas = suggestIdeas(campaign, campaign.batchSize ?? 3, Date.now());
          const from = Math.max(campaign.nextBatch ?? DEMO_TODAY + 1, DEMO_TODAY + 1);
          const taken: Post[] = [];
          slots = ideas.flatMap((idea) => {
            const day = freeDay(
              [...stateRef.current.posts, ...taken],
              campaign.workspaceId,
              idea.channel,
              from,
              (campaign.end ?? from + 14) + 3
            );
            if (!day) return [];
            taken.push({
              workspaceId: campaign.workspaceId,
              channel: idea.channel,
              day,
              status: 'Planned',
            } as Post);
            return [{ day, channel: idea.channel, format: idea.format, title: idea.title }];
          });
        }
        return draftSlots(campaign, slots, actor, { samples: false });
      },
      ensureCampaignConversation(campaignId: string) {
        const campaign = stateRef.current.campaigns.find((item) => item.id === campaignId);
        if (!campaign) return undefined;
        if (
          campaign.conversationId &&
          stateRef.current.conversations.some(
            (conversation) => conversation.id === campaign.conversationId
          )
        )
          return campaign.conversationId;
        const id = `conv-${campaignId}`;
        setState((current) => ({
          ...current,
          campaigns: current.campaigns.map((item) =>
            item.id === campaignId ? { ...item, conversationId: id } : item
          ),
          tasks: current.tasks.map((task) =>
            task.campaignId === campaignId && !task.conversationId
              ? { ...task, conversationId: id }
              : task
          ),
          conversations: current.conversations.some((conversation) => conversation.id === id)
            ? current.conversations
            : [
                ...current.conversations,
                {
                  id,
                  workspaceId: campaign.workspaceId,
                  title: campaign.name,
                  showAsWritten: true,
                  scope: 'campaign',
                  unread: 0,
                  messages: [
                    {
                      id: nextMessageId(),
                      from: 'amy',
                      at: nowLabel(),
                      text: 'This is the chat for “{name}”. Ask me anything about the campaign here.',
                      variables: { name: campaign.name },
                    },
                  ],
                },
              ],
        }));
        return id;
      },
      updatePlan(campaignId: string, plan: Campaign['plan']) {
        setState((current) => ({
          ...current,
          campaigns: current.campaigns.map((campaign) =>
            campaign.id === campaignId ? { ...campaign, plan } : campaign
          ),
        }));
      },
      saveTemplate(campaignId: string) {
        const campaign = stateRef.current.campaigns.find((item) => item.id === campaignId);
        if (!campaign) return;
        const firstCampaignPost = stateRef.current.posts.find(
          (post) => post.campaign === campaign.key && post.workspaceId === campaign.workspaceId
        );
        const formats = [
          ...new Set([
            ...(campaign.plan ?? []).map((planSlot) => planSlot.format),
            ...campaign.ideas.map((idea) => idea.format),
          ]),
        ] as Format[];
        setState((current) => ({
          ...current,
          templates: [
            ...current.templates,
            {
              id: `tpl-${Date.now()}`,
              name: campaign.name,
              goal: campaign.goal,
              description: campaign.brief.split('\n')[0].split('. ')[0],
              inputs: ['What is new', 'Dates', 'One photo'],
              formats: formats.length ? formats : ['Photo'],
              example: {
                title: firstCampaignPost?.title ?? campaign.name,
                caption: firstCampaignPost?.caption ?? campaign.brief,
              },
              by: 'Hakusana',
            },
          ],
        }));
      },
      endCampaign(campaignId: string, stopScheduled: boolean, actor: Actor) {
        const campaign = stateRef.current.campaigns.find(
          (candidateCampaign) => candidateCampaign.id === campaignId
        );
        if (!campaign) return;
        const at = nowLabel();
        setState((current) => ({
          ...current,
          campaigns: current.campaigns.map((existingCampaign) =>
            existingCampaign.id === campaignId
              ? { ...existingCampaign, status: 'Finished' as const }
              : existingCampaign
          ),
          revising: current.revising.filter(
            (job) => current.posts.find((post) => post.id === job.postId)?.campaign !== campaign.key
          ),
          posts: current.posts.map((post) => {
            if (
              post.workspaceId !== campaign.workspaceId ||
              post.campaign !== campaign.key ||
              post.day <= 18
            )
              return post;
            if (
              ['Planned', 'AMY is working', 'Needs your review', 'Needs your input'].includes(
                post.status
              )
            )
              return {
                ...post,
                status: 'Cancelled' as const,
                history: [
                  ...post.history,
                  { at, by: actor.name, text: 'Cancelled when the campaign ended' },
                ],
              };
            if (stopScheduled && post.status === 'Scheduled')
              return {
                ...post,
                status: 'Approved · Stopped' as const,
                history: [
                  ...post.history,
                  { at, by: actor.name, text: 'Stopped when the campaign ended' },
                ],
              };
            return post;
          }),
        }));
        logToChat(campaign.workspaceId, '{name} ended the campaign “{title}”.', {
          name: actor.name,
          title: campaign.name,
        });
      },
      markConversationRead(conversationId: string) {
        setState((current) => ({
          ...current,
          conversations: current.conversations.map((item) =>
            item.id === conversationId ? { ...item, unread: 0 } : item
          ),
        }));
      },
      markNoticesRead(workspaceId: string) {
        setState((current) => ({
          ...current,
          notices: current.notices.map((item) =>
            item.workspaceId === workspaceId ? { ...item, read: true } : item
          ),
        }));
      },
      retryRevision(id: string) {
        updatePost(id, () => ({ status: 'AMY is working' }));
        setState((current) => ({
          ...current,
          revising: [
            ...current.revising,
            {
              postId: id,
              readyAt: Date.now() + 4000,
              note: 'Retried after the earlier revision failed.',
            },
          ],
        }));
      },
      remind(key: string) {
        setState((current) => ({
          ...current,
          reminders: { ...current.reminders, [key]: nowLabel() },
        }));
      },
      clearReminder(key: string) {
        setState((current) => {
          const reminders = { ...current.reminders };
          delete reminders[key];
          return { ...current, reminders };
        });
      },
      addUploads(
        workspaceId: string,
        files: { name: string; kind: Asset['kind']; sizeMb: number; src?: string }[],
        forPost?: string
      ) {
        setState((current) => {
          const existingAssets = current.assets.filter(
            (asset) => asset.workspaceId === workspaceId
          );
          const uploads: Upload[] = files.map((file, fileIndex) => {
            const duplicate = existingAssets.find(
              (existingAsset) =>
                existingAsset.file === file.name ||
                existingAsset.name === file.name.replace(/\.[^.]+$/, '')
            );
            const unsupported = /\.(psd|ai|exe|zip|indd)$/i.test(file.name);
            return {
              id: `u-${Date.now()}-${fileIndex}`,
              workspaceId: workspaceId as WorkspaceId,
              name: file.name,
              kind: file.kind,
              sizeMb: file.sizeMb,
              src: file.src,
              forPost,
              progress: 0,
              state: unsupported ? 'unsupported' : duplicate ? 'duplicate' : 'uploading',
              note: unsupported
                ? 'This file type cannot be used in posts. Export a JPG, PNG, MP4 or PDF instead.'
                : duplicate
                  ? 'Already in the Material bank as “{name}”.'
                  : undefined,
              noteVars: duplicate ? { name: duplicate.name } : undefined,
            };
          });
          return { ...current, uploads: [...uploads, ...current.uploads] };
        });
      },
      setUploadState(
        id: string,
        uploadAction: 'pause' | 'resume' | 'retry' | 'cancel' | 'dismiss' | 'keepBoth'
      ) {
        setState((current) => ({
          ...current,
          uploads:
            uploadAction === 'dismiss'
              ? current.uploads.filter((upload) => upload.id !== id)
              : current.uploads.map((upload) => {
                  if (upload.id !== id) return upload;
                  if (uploadAction === 'pause' && upload.state === 'uploading')
                    return {
                      ...upload,
                      state: 'paused' as const,
                      note: 'Paused. Resume any time; it continues where it stopped.',
                    };
                  if (
                    (uploadAction === 'resume' && upload.state === 'paused') ||
                    (uploadAction === 'retry' && upload.state === 'failed') ||
                    uploadAction === 'keepBoth'
                  )
                    return {
                      ...upload,
                      state: 'uploading' as const,
                      note: undefined,
                      noteVars: undefined,
                    };
                  if (uploadAction === 'cancel')
                    return {
                      ...upload,
                      state: 'cancelled' as const,
                      note: 'Cancelled. Nothing was added.',
                    };
                  return upload;
                }),
        }));
      },
      interruptUpload(id: string) {
        setState((current) => ({
          ...current,
          uploads: current.uploads.map((upload) =>
            upload.id === id && upload.state === 'uploading'
              ? {
                  ...upload,
                  state: 'failed' as const,
                  note: 'The connection dropped at {p}%. Retry continues from where it stopped.',
                  noteVars: { p: String(upload.progress) },
                }
              : upload
          ),
        }));
      },
      updateAsset(id: string, patch: Partial<Asset>) {
        setState((current) => ({
          ...current,
          assets: current.assets.map((asset) => (asset.id === id ? { ...asset, ...patch } : asset)),
        }));
      },
      connectDrive(workspaceId: string, folder: string, account: string, fileCount: number) {
        setState((current) => ({
          ...current,
          drive: current.drive.map((driveLink) =>
            driveLink.workspaceId === workspaceId
              ? {
                  ...driveLink,
                  connected: true,
                  folder,
                  account,
                  access: 'ok' as const,
                  lastChecked: nowLabel(),
                  fileCount,
                }
              : driveLink
          ),
        }));
      },
      disconnectDrive(workspaceId: string) {
        setState((current) => ({
          ...current,
          drive: current.drive.map((driveLink) =>
            driveLink.workspaceId === workspaceId
              ? { ...driveLink, connected: false, access: 'ok' as const }
              : driveLink
          ),
        }));
      },
      refreshDrive(workspaceId: string) {
        setState((current) => ({
          ...current,
          drive: current.drive.map((driveLink) =>
            driveLink.workspaceId === workspaceId
              ? { ...driveLink, lastChecked: nowLabel() }
              : driveLink
          ),
        }));
      },
      setDriveAccess(workspaceId: string, access: 'ok' | 'lost') {
        setState((current) => ({
          ...current,
          drive: current.drive.map((driveLink) =>
            driveLink.workspaceId === workspaceId
              ? { ...driveLink, access, lastChecked: nowLabel() }
              : driveLink
          ),
        }));
      },
      useDriveFiles(
        workspaceId: string,
        files: { name: string; kind: Asset['kind']; image?: string; sizeMb: number }[]
      ) {
        const driveImportTimestamp = Date.now();
        const filesNotYetInMaterialBank = files.filter(
          (file) =>
            !stateRef.current.assets.some(
              (asset) => asset.workspaceId === workspaceId && asset.file === file.name
            )
        );
        const ids = filesNotYetInMaterialBank.map(
          (_, fileIndex) => `drive-${driveImportTimestamp}-${fileIndex}`
        );
        setState((current) => {
          const added: Asset[] = filesNotYetInMaterialBank.map((file, fileIndex) => {
            const id = ids[fileIndex];
            return {
              id,
              workspaceId: workspaceId as WorkspaceId,
              name: file.name.replace(/\.[^.]+$/, ''),
              kind: file.kind,
              file: file.name,
              image: file.image ? `${file.image}#${id}` : '',
              size: `${file.sizeMb} MB`,
              badges: ['Linked', 'Preparing media'],
              source: 'Drive',
              added: nowLabel(),
              preparingUntil: Date.now() + 3500 + fileIndex * 800,
              tags: [],
            };
          });
          return { ...current, assets: [...current.assets, ...added] };
        });
        return ids;
      },
      resolveMissing(postId: string, method: 'clips' | 'photos', names: string[], actor: Actor) {
        const post = stateRef.current.posts.find((item) => item.id === postId);
        if (!post) return;
        updatePost(postId, (postToUpdate) => ({
          status: 'AMY is working',
          missing: undefined,
          format: method === 'photos' ? 'Carousel' : postToUpdate.format,
          history: [
            ...postToUpdate.history,
            {
              at: nowLabel(),
              by: actor.name,
              text: method === 'clips' ? 'Added clips: {names}' : 'Chose photos instead: {names}',
              variables: { names: names.join(', ') },
            },
          ],
        }));
        setState((current) => ({
          ...current,
          revising: [
            ...current.revising,
            {
              postId,
              readyAt: Date.now() + 6000,
              note:
                method === 'clips'
                  ? 'Made with the clips you added.'
                  : 'Rebuilt as a photo carousel with the photos you chose.',
            },
          ],
        }));
        logToChat(
          post.workspaceId,
          method === 'clips'
            ? '{name} added clips for “{title}”. AMY is preparing it.'
            : '{name} chose photos instead of video for “{title}”. AMY is preparing a carousel.',
          { name: actor.name, title: post.title },
          postId
        );
      },
      reset() {
        runIgnoringUnavailableStorage(() => window.localStorage.removeItem(STORAGE_KEY));
        setState(seedState());
      },
      resetNewClient() {
        const freshSeedState = seedState();
        setState((current) => {
          const replaceNewClientItemsWithSeed = <T extends { workspaceId: string }>(
            currentItems: T[],
            seedItems: T[]
          ) => [
            ...currentItems.filter((item) => item.workspaceId !== NEW_CLIENT_WORKSPACE_ID),
            ...seedItems.filter((item) => item.workspaceId === NEW_CLIENT_WORKSPACE_ID),
          ];
          const welcome = { ...current.welcome };
          delete welcome[NEW_CLIENT_WORKSPACE_ID];
          const slotOverrides = { ...current.dailyLimitOverrides };
          delete slotOverrides[NEW_CLIENT_WORKSPACE_ID];
          return {
            ...current,
            posts: replaceNewClientItemsWithSeed(current.posts, freshSeedState.posts),
            accounts: replaceNewClientItemsWithSeed(current.accounts, freshSeedState.accounts),
            assets: replaceNewClientItemsWithSeed(current.assets, freshSeedState.assets),
            conversations: replaceNewClientItemsWithSeed(
              current.conversations,
              freshSeedState.conversations
            ),
            brands: replaceNewClientItemsWithSeed(current.brands, freshSeedState.brands),
            settings: replaceNewClientItemsWithSeed(current.settings, freshSeedState.settings),
            notices: replaceNewClientItemsWithSeed(current.notices, freshSeedState.notices),
            uploads: replaceNewClientItemsWithSeed(current.uploads, freshSeedState.uploads),
            drive: replaceNewClientItemsWithSeed(current.drive, freshSeedState.drive),
            campaigns: replaceNewClientItemsWithSeed(current.campaigns, freshSeedState.campaigns),
            tasks: replaceNewClientItemsWithSeed(current.tasks, freshSeedState.tasks),
            members: replaceNewClientItemsWithSeed(current.members, freshSeedState.members),
            business: replaceNewClientItemsWithSeed(current.business, freshSeedState.business),
            aiConnections: replaceNewClientItemsWithSeed(
              current.aiConnections,
              freshSeedState.aiConnections
            ),
            helpers: replaceNewClientItemsWithSeed(current.helpers, freshSeedState.helpers),
            onboarding: replaceNewClientItemsWithSeed(
              current.onboarding,
              freshSeedState.onboarding
            ),
            requests: replaceNewClientItemsWithSeed(current.requests, freshSeedState.requests),
            revising: current.revising.filter(
              (revisionJob) =>
                current.posts.find((post) => post.id === revisionJob.postId)?.workspaceId !==
                NEW_CLIENT_WORKSPACE_ID
            ),
            welcome,
            dailyLimitOverrides: slotOverrides,
          };
        });
      },
      saveWelcome(workspaceId: string, patch: Partial<WelcomeDraft>) {
        setState((current) => ({
          ...current,
          welcome: {
            ...current.welcome,
            [workspaceId]: { ...(current.welcome[workspaceId] ?? emptyDraft()), ...patch },
          },
        }));
      },
      finishWelcome(
        workspaceId: string,
        welcomeChoices: {
          look: string;
          captions: string;
          caption: string;
          image: string;
          channel: Channel;
          language: 'pl' | 'en';
          title: string;
          tone: string;
        },
        actor: Actor
      ): string {
        const latestState = stateRef.current;
        const welcomeDraft = latestState.welcome[workspaceId] ?? emptyDraft();
        const typedWorkspaceId = workspaceId as WorkspaceId;
        const day = DEMO_TODAY + 2;
        const drafted = draftPost({
          workspaceId: typedWorkspaceId,
          title: welcomeChoices.title,
          channel: welcomeChoices.channel,
          format: 'Photo',
          day,
          accounts: latestState.accounts,
          assets: [],
          language: welcomeChoices.language,
        });
        const hasBusinessProfileForChannel = latestState.accounts.some(
          (account) =>
            account.workspaceId === workspaceId &&
            account.network === welcomeChoices.channel &&
            !/personal/i.test(account.type)
        );
        const post = {
          ...drafted,
          account: hasBusinessProfileForChannel ? drafted.account : '',
          image: welcomeChoices.image,
          caption: welcomeChoices.caption,
          time: '10:00',
          history: [{ at: nowLabel(), by: 'AMY', text: 'Made from your setup choices' }],
        };
        const colourRoles: BrandSettings['colours'][number]['role'][] = [
          'Primary',
          'Background',
          'Text',
          'Accent',
          'Light accent',
        ];
        const keptColours = welcomeDraft.colours.filter((colour) => colour.keep);
        const activeBrandSettings: BrandSettings = {
          ...newClientBrand,
          oneLine: welcomeChoices.look,
          positioning: welcomeDraft.about,
          assets: welcomeDraft.logo
            ? [
                {
                  id: 'main',
                  label: 'Main logo',
                  kind: 'Logo',
                  src: welcomeDraft.logo.src,
                  source: 'Uploaded',
                  file: welcomeDraft.logo.name,
                  bg: 'light',
                },
              ]
            : [],
          colours: keptColours.map((colour, colourIndex) => ({
            name: `Colour ${colourIndex + 1}`,
            role: colourRoles[colourIndex] ?? 'Custom',
            hex: colour.hex,
          })),
          tones: [welcomeChoices.tone],
          languages:
            welcomeDraft.language === 'both'
              ? [
                  { code: 'PL', title: 'Polish', note: '' },
                  { code: 'EN', title: 'English', note: '' },
                ]
              : [
                  {
                    code: welcomeChoices.language.toUpperCase(),
                    title: welcomeChoices.language === 'pl' ? 'Polish' : 'English',
                    note: '',
                  },
                ],
          notes: `Look: ${welcomeChoices.look}\nCaptions: ${welcomeChoices.captions}`,
          photoStyle: [
            ...welcomeDraft.likes,
            ...(welcomeDraft.likeText.trim() ? [welcomeDraft.likeText.trim()] : []),
          ],
          words: {
            use: [],
            avoid: welcomeDraft.avoid
              .split(/[,;\n]/)
              .map((avoidedWord) => avoidedWord.trim())
              .filter(Boolean),
          },
          examples: [
            {
              name: `${welcomeChoices.title} · ${welcomeChoices.channel}`,
              image: welcomeChoices.image,
              why: 'Chosen during setup',
              by: actor.name,
              day: DEMO_TODAY,
              caption: welcomeChoices.caption,
            },
          ],
        };
        const assets: Asset[] = [welcomeDraft.logo, welcomeDraft.photo]
          .filter(Boolean)
          .map((uploadedFile, assetIndex) => ({
            id: `nowa-a${Date.now()}-${assetIndex}`,
            workspaceId: typedWorkspaceId,
            name: assetIndex === 0 && welcomeDraft.logo ? 'Logo' : 'Photo you liked',
            kind: 'Photo' as const,
            image: uploadedFile!.src,
            badges: [],
            file: uploadedFile!.name,
            source: 'Uploaded' as const,
            added: `${DEMO_TODAY} Nov`,
          }));
        setState((current) => ({
          ...current,
          posts: [...current.posts, post],
          assets: [...current.assets, ...assets],
          business: current.business.map((business) =>
            business.workspaceId === typedWorkspaceId
              ? {
                  ...business,
                  name: welcomeDraft.name,
                  website: websiteWithProtocol(welcomeDraft.website),
                  products: welcomeDraft.about,
                  updatedBy: actor.name,
                  updatedAt: `${DEMO_TODAY} Nov`,
                }
              : business
          ),
          brands: current.brands.map((brand) =>
            brand.workspaceId === typedWorkspaceId
              ? {
                  ...brand,
                  version: 1,
                  active: activeBrandSettings,
                  draft: null,
                  history: [
                    {
                      version: 1,
                      by: actor.name,
                      day: DEMO_TODAY,
                      summary: 'Created during setup',
                      snapshot: activeBrandSettings,
                    },
                  ],
                }
              : brand
          ),
          onboarding: current.onboarding.map((onboarding) =>
            onboarding.workspaceId === typedWorkspaceId
              ? { ...onboarding, samples: [post.id] }
              : onboarding
          ),
          welcome: {
            ...current.welcome,
            [typedWorkspaceId]: {
              ...welcomeDraft,
              done: true,
              postId: post.id,
              summaryLook: welcomeChoices.look,
              summaryCaption: welcomeChoices.captions,
            },
          },
        }));
        logToChat(
          typedWorkspaceId,
          '{name} finished getting started. AMY saved the style and made the first post for review.',
          { name: actor.name }
        );
        return post.id;
      },
    }),
    [updatePost, logToChat, draftSlots]
  );

  useEffect(() => {
    const campaignReadyForAutomaticBatch = state.campaigns.find(
      (campaign) =>
        campaign.plan &&
        campaign.samplesApproved === false &&
        !campaign.creationPaused &&
        campaign.status !== 'Finished' &&
        areAllSamplePostsApproved(campaign, state.posts)
    );
    if (!campaignReadyForAutomaticBatch) return;
    const automaticBatchTimeoutId = window.setTimeout(() => {
      setState((current) => ({
        ...current,
        campaigns: current.campaigns.map((campaign) =>
          campaign.id === campaignReadyForAutomaticBatch.id
            ? { ...campaign, samplesApproved: true }
            : campaign
        ),
      }));
      commands.runBatch(campaignReadyForAutomaticBatch.id, 'AMY');
    }, 1500);
    return () => window.clearTimeout(automaticBatchTimeoutId);
  }, [state.campaigns, state.posts, commands]);

  return { state, commands };
}

type StoreValue = ReturnType<typeof useDemoStoreValue>;
export const DemoStoreContext = createContext<StoreValue | null>(null);

export function useDemo() {
  const value = useContext(DemoStoreContext);
  if (!value) throw new Error('useDemo must be used inside DemoStoreProvider');
  return value;
}

export function useWorkspaceData(workspaceId: string) {
  const { state } = useDemo();
  return useMemo(
    () => ({
      workspace: workspaceShowingNewClientBusinessName(workspaceId, state.business),
      welcome: state.welcome[workspaceId] ?? emptyDraft(),
      posts: state.posts.filter((post) => post.workspaceId === workspaceId),
      accounts: state.accounts.filter((item) => item.workspaceId === workspaceId),
      assets: state.assets.filter((item) => item.workspaceId === workspaceId),
      conversations: state.conversations.filter((item) => item.workspaceId === workspaceId),
      brand: state.brands.find((item) => item.workspaceId === workspaceId)!,
      settings: state.settings.find((item) => item.workspaceId === workspaceId)!,
      notices: state.notices.filter((item) => item.workspaceId === workspaceId),
      uploads: state.uploads.filter((item) => item.workspaceId === workspaceId),
      drive: state.drive.find((item) => item.workspaceId === workspaceId)!,
      campaigns: state.campaigns.filter((item) => item.workspaceId === workspaceId),
      tasks: state.tasks.filter((item) => item.workspaceId === workspaceId),
      templates: state.templates,
      members: state.members.filter((item) => item.workspaceId === workspaceId),
      staff: state.staff,
      business: state.business.find((item) => item.workspaceId === workspaceId)!,
      aiConnection: state.aiConnections.find((item) => item.workspaceId === workspaceId)!,
      helpers: state.helpers.filter((item) => item.workspaceId === workspaceId),
      onboarding: state.onboarding.find((item) => item.workspaceId === workspaceId)!,
      requests: state.requests.filter((item) => item.workspaceId === workspaceId),
      dailyLimitOverrides: state.dailyLimitOverrides[workspaceId] ?? {},
    }),
    [state, workspaceId]
  );
}
